/**
 * Português do Brasil — a FONTE DA VERDADE do dicionário.
 *
 * As chaves nascem aqui, e os outros idiomas são obrigados a ter exatamente
 * as mesmas (o tipo `Dicionario` em `index.ts` exige; o teste de paridade
 * confere de novo em tempo de execução). Uma chave nova entra aqui primeiro.
 *
 * `as const` é o que faz o typecheck conhecer cada chave e cada `{parâmetro}`
 * dentro do texto: `t('lobby.entrar')` compila, `t('lobby.entrarr')` não, e
 * `t('emJogo.prazoEm')` sem `{ min }` também não.
 *
 * Convenções:
 *   - `{nome}` é um parâmetro. Quem chama passa o valor; a interpolação NÃO
 *     escapa HTML, então em texto que vira `innerHTML` o chamador escapa antes.
 *   - `{ one, other }` é plural mínimo, escolhido por `count`.
 *   - `evento.<código>` traduz um código de evento do servidor (o catálogo
 *     está em `@sol-pool/protocol`). Um teste exige que todo código tenha
 *     tradução aqui e em `en`.
 *   - `erro.codigo.<ErrorCode>` é o fallback quando o servidor manda só a
 *     classe do erro, sem evento — servidor antigo ou caminho ainda não coberto.
 */
export const ptBR = {
  // ------------------------------------------------------------------ comum
  'comum.verNaChain': 'ver na chain ↗',
  'comum.lendoBlockchain': 'Lendo da blockchain…',
  'comum.carregando': 'Carregando…',
  'comum.aguardando': 'aguardando…',

  // ----------------------------------------------------------------- idioma
  'idioma.rotulo': 'Idioma',
  'idioma.pt-BR': 'Português',
  'idioma.en': 'English',

  // --------------------------------------------------------- erros locais
  'erro.recusouSolicitacao': 'Você recusou a solicitação na carteira.',
  'erro.semCarteira': 'Nenhuma carteira Solana encontrada neste navegador.',
  'erro.inesperado': 'Erro inesperado.',
  'erro.saldoInsuficiente':
    'Saldo insuficiente: a transação precisa de {precisa} SOL e a carteira tem {tem}. Nada foi cobrado.',
  'erro.recusouAssinatura': 'Você recusou a assinatura na carteira. Nada foi enviado.',
  'erro.expirou':
    'A transação demorou demais e expirou antes de entrar num bloco. Tente de novo.',
  'erro.limiteRpc':
    'A rede recusou a chamada por excesso de uso momentâneo. Espere alguns segundos e tente de novo — nada foi enviado.',
  'erro.servidorNaoRespondeu': 'O servidor não respondeu a tempo.',
  // A transação ENTROU num bloco e foi rejeitada pela rede (`value.err`): nada
  // foi transferido, e por isso a frase diz que nada foi cobrado.
  'erro.transacaoFalhou':
    'A transação entrou na rede e falhou. Nada foi cobrado — tente de novo.',

  // ------------------------------------------- classe de erro do servidor
  'erro.codigo.unauthenticated': 'Assine o login antes de operar salas.',
  'erro.codigo.bad_request': 'O servidor não entendeu a mensagem.',
  'erro.codigo.nonce_invalid': 'A sessão de login não vale mais. Assine de novo.',
  'erro.codigo.signature_invalid': 'A assinatura do login não confere.',
  'erro.codigo.stake_out_of_range': 'Valor de entrada fora dos limites.',
  'erro.codigo.deposit_not_found': 'Depósito não encontrado na blockchain.',
  'erro.codigo.deposit_mismatch': 'O depósito na blockchain não confere com a mesa.',
  'erro.codigo.room_not_found': 'Sala não encontrada.',
  'erro.codigo.room_not_joinable': 'Esta sala não aceita mais jogadores.',
  'erro.codigo.already_in_room': 'Você já está em uma sala.',
  'erro.codigo.not_room_creator': 'Só quem criou pode cancelar.',
  'erro.codigo.no_reservation': 'Nenhuma reserva para confirmar.',
  'erro.codigo.chain_error': 'Falha ao ler a blockchain.',
  'erro.codigo.rate_limited': 'Muitas mensagens em pouco tempo. Espere um instante.',
  'erro.codigo.voice_off': 'Esta mesa está sem voz.',
  'erro.codigo.voice_not_allowed': 'Você não está nesta mesa.',
  'erro.codigo.voice_unavailable': 'A voz está indisponível agora.',
  'erro.codigo.internal': 'Erro interno.',
  'erro.codigo.engine_unverified':
    'O motor deste navegador ainda não passou pela verificação de determinismo. Apostas ficam fechadas nele.',
  'erro.codigo.match_not_found': 'Partida não encontrada.',
  'erro.codigo.not_your_turn': 'Não é a sua vez.',
  'erro.codigo.wrong_phase': 'A partida não aceita isso agora.',
  'erro.codigo.bad_shot': 'Tacada inválida.',
  'erro.codigo.bad_decision': 'Escolha inválida.',
  'erro.codigo.unknown_player': 'Esta carteira não está nesta partida.',
  'erro.codigo.already_revealed': 'Você já fez isso nesta partida.',
  'erro.codigo.bad_reveal': 'O nonce não corresponde ao compromisso enviado.',
  'erro.codigo.commit_mismatch': 'O compromisso desta partida não é o que está na blockchain.',
  'erro.codigo.spectators_disabled': 'Esta mesa não aceita espectadores.',
  'erro.codigo.chat_too_long': 'A mensagem é longa demais.',
  'erro.codigo.chat_too_fast': 'Espere um instante antes de mandar outra.',
  'erro.codigo.not_in_match': 'Você não está em nenhuma mesa.',
  'erro.codigo.evento_nao_encontrado': 'Evento não encontrado.',
  'erro.codigo.evento_cheio': 'Este evento já está cheio.',
  'erro.codigo.evento_fechado': 'Este evento já começou.',
  'erro.codigo.ja_em_evento': 'Você já está num evento.',
  'erro.codigo.fora_do_evento': 'Você não está em nenhum evento.',
  'erro.codigo.formato_invalido': 'Formato de mesa desconhecido.',
  'erro.codigo.em_manutencao': 'O servidor vai ser atualizado: não dá para criar nem entrar em mesas agora.',
  'erro.codigo.password_required': 'Esta mesa tem senha.',
  'erro.codigo.password_incorrect': 'Senha incorreta.',
  'erro.codigo.password_invalid': 'A senha está fora do tamanho permitido.',
  'erro.codigo.password_attempts': 'Muitas tentativas de senha. Espere um pouco.',
  'erro.codigo.invite_not_found': 'Nenhuma mesa com este código.',
  'erro.codigo.invite_invalid': 'Código de convite inválido.',
  'erro.codigo.invite_attempts': 'Muitos códigos em pouco tempo. Espere um pouco.',
  'erro.codigo.nickname_invalid': 'Apelido fora do formato permitido.',
  'erro.codigo.nickname_forbidden': 'Este apelido não é permitido.',
  'erro.codigo.nickname_attempts': 'Muitas trocas de apelido. Espere um pouco.',

  // --------------------------------------------------- eventos do servidor
  'evento.auth.falhou': 'Falha ao autenticar.',
  'evento.auth.necessaria': 'Assine o login antes de operar salas.',
  'evento.auth.endereco_invalido': 'Endereço de carteira inválido.',
  'evento.auth.nonce_desconhecido': 'Nonce desconhecido ou já usado.',
  'evento.auth.nonce_expirado': 'Nonce expirado.',
  'evento.auth.nonce_de_outra_carteira': 'Nonce emitido para outra carteira.',
  'evento.auth.mensagem_nao_confere': 'Mensagem assinada não confere.',
  'evento.auth.assinatura_mal_formada': 'Assinatura mal formada.',
  'evento.auth.assinatura_invalida': 'Assinatura inválida.',
  'evento.auth.sessao_desconhecida': 'Sessão desconhecida.',
  'evento.auth.sessao_expirada': 'Sessão expirada.',

  'evento.geral.interno': 'Erro interno.',
  'evento.geral.mensagem_invalida': 'Mensagem inválida.',

  'evento.motor.webkit_nao_verificado':
    'O determinismo da física ainda não foi verificado no WebKit (Safari, e todo navegador no iPhone). Enquanto não for, partidas valendo SOL ficam fechadas — jogar sem aposta continua liberado.',
  'evento.motor.desconhecido':
    'Não foi possível identificar o motor deste navegador, e só liberamos os que passaram na verificação de determinismo. Use Firefox ou Chrome para apostar.',

  'evento.lobby.entrada_fora_dos_limites': 'Valor de entrada fora dos limites.',
  'evento.lobby.ja_em_sala': 'Você já está em uma sala.',
  'evento.lobby.sem_reserva_de_mesa': 'Nenhuma reserva de mesa para confirmar.',
  'evento.lobby.reserva_vencida': 'A reserva venceu. Comece de novo — o depósito volta pelo prazo da blockchain.',
  'evento.lobby.sem_reserva_de_entrada': 'Nenhuma reserva de entrada para confirmar.',
  'evento.lobby.deposito_nao_encontrado': 'Depósito não encontrado na blockchain.',
  'evento.lobby.deposito_de_outra_carteira': 'O depósito foi feito por outra carteira.',
  'evento.lobby.deposito_valor_diferente': 'O valor depositado não confere com o reservado.',
  'evento.lobby.prazo_curto_demais':
    'O prazo desta partida é curto demais para uma partida inteira; cancele e crie a mesa de novo.',
  'evento.lobby.manutencao':
    'O servidor vai ser atualizado: não dá para criar nem entrar em mesas agora. Quem já está jogando termina a partida.',
  'manutencao.faixa':
    'Atualização em instantes: não é possível criar nem entrar em mesas agora. Quem já está jogando termina a partida.',
  'manutencao.faixaComHora':
    'Atualização prevista para {hora}: não é possível criar nem entrar em mesas agora. Quem já está jogando termina a partida.',
  'evento.lobby.sala_nao_encontrada': 'Sala não encontrada.',
  'evento.lobby.sala_cheia': 'Esta sala não aceita mais jogadores.',
  'evento.eventomesa.nao_encontrado': 'Evento não encontrado.',
  'evento.eventomesa.cheio': 'Este evento já está cheio.',
  'evento.eventomesa.fechado': 'Este evento já começou.',
  'evento.eventomesa.ja_em_evento': 'Você já está num evento.',
  'evento.eventomesa.fora': 'Você não está em nenhum evento.',
  'evento.eventomesa.formato_invalido': 'Formato de mesa desconhecido.',
  'evento.eventomesa.entrada_invalida': 'Valor de entrada inválido.',
  'evento.eventomesa.inscricao_nao_paga': 'A inscrição ainda não foi paga.',
  'evento.eventomesa.pago_indisponivel': 'Torneio com entrada ainda não está disponível.',
  'evento.eventomesa.id_em_uso': 'Este torneio já existe.',
  'evento.eventomesa.nao_e_o_dono': 'Só quem criou o torneio pode cancelar.',
  'evento.eventomesa.ocupado_em_mesa': 'Termine ou cancele a sua mesa antes de entrar num evento.',
  'evento.eventomesa.sair_depois_de_comecar': 'O evento já começou: quem sai agora perde pelo relógio.',
  'evento.lobby.propria_mesa': 'Você não pode entrar na própria mesa.',
  'evento.lobby.prazo_vencido': 'O prazo desta mesa venceu na blockchain.',
  'evento.lobby.partida_nao_encontrada_na_chain': 'Partida não encontrada na blockchain.',
  'evento.lobby.deposito_do_oponente_pendente': 'O depósito do oponente ainda não confirmou.',
  'evento.lobby.entrou_outra_carteira': 'Quem entrou na partida foi outra carteira.',
  'evento.lobby.so_criador_cancela': 'Só quem criou pode cancelar.',
  'evento.lobby.mesa_ja_comprometida': 'A mesa já está comprometida.',
  'evento.lobby.partida_ainda_na_chain': 'A partida ainda existe na blockchain.',
  'evento.lobby.falha_ao_ler_chain': 'Falha ao ler a blockchain: {erro}',
  'evento.lobby.senha_obrigatoria': 'Esta mesa tem senha. Peça a quem criou.',
  'evento.lobby.senha_incorreta': 'Senha incorreta.',
  'evento.lobby.senha_invalida': 'A senha deve ter entre {min} e {max} caracteres.',
  'evento.lobby.senha_muitas_tentativas':
    'Muitas tentativas nesta mesa. Espere {segundos} s antes de tentar de novo.',
  'evento.lobby.codigo_nao_encontrado': 'Nenhuma mesa aberta com este código. Confira com quem convidou.',
  'evento.lobby.codigo_invalido': 'Código inválido: são 6 caracteres, sem 0, O, 1 ou I.',
  'evento.lobby.codigo_muitas_tentativas': 'Muitos códigos em pouco tempo. Espere {segundos} s.',
  'evento.perfil.apelido_invalido':
    'O apelido deve ter de {min} a {max} caracteres: letras, números, espaço, _ ou -.',
  'evento.perfil.apelido_proibido': 'Este apelido não é permitido. Escolha outro.',
  'evento.perfil.muitas_trocas': 'Muitas trocas de apelido. Espere {segundos} s.',

  'evento.partida.nenhuma': 'Você não está em nenhuma partida.',
  'evento.partida.ja_em_partida': 'Você já está em outra partida.',
  'evento.partida.ja_comprometido': 'Você já se comprometeu.',
  'evento.partida.quebra_ja_definida': 'A quebra já foi definida.',
  'evento.partida.ja_revelou': 'Este jogador já revelou.',
  'evento.partida.nonce_nao_confere': 'O nonce não corresponde ao compromisso enviado.',
  'evento.partida.compromisso_diverge_da_chain':
    'O compromisso desta partida não é o que está na blockchain. Isso costuma acontecer ao depositar num navegador e jogar em outro, ou com os dados do site apagados. Abra a partida no mesmo navegador em que você depositou.',
  'evento.partida.nao_espera_tacada': 'A partida não está esperando uma tacada.',
  'evento.partida.nao_e_sua_vez': 'Não é a sua vez.',
  'evento.partida.coloque_a_branca': 'Coloque a branca antes de jogar.',
  'evento.partida.limite_gravavel': 'A partida atingiu o limite gravável.',
  'evento.partida.declare_a_cacapa': 'Declare a caçapa antes de tacar.',
  'evento.partida.sem_bola_na_mao': 'Não há bola na mão agora.',
  'evento.partida.bola_na_mao_nao_e_sua': 'A bola na mão não é sua.',
  'evento.partida.posicao_invalida': 'Posição inválida.',
  'evento.partida.branca_atras_da_linha':
    'Falta na quebra: a branca vai atrás da linha da cabeça.',
  'evento.partida.sem_decisao_pendente': 'Não há decisão pendente.',
  'evento.partida.escolha_nao_e_sua': 'A escolha não é sua.',
  'evento.partida.opcao_inexistente': 'Opção {opcao} não existe.',
  'evento.partida.ainda_nao_comecou':
    'A partida ainda não começou; revele o nonce ou espere o prazo da revelação.',
  'evento.partida.carteira_fora_da_partida': 'Esta carteira não está nesta partida.',
  'evento.partida.angulo_fora_da_faixa': 'Ângulo fora da faixa: {valor}.',
  'evento.partida.forca_fora_da_faixa': 'Força fora da faixa: {valor}.',
  'evento.partida.efeito_fora_da_faixa': 'Efeito fora da faixa em {eixo}: {valor}.',
  'evento.partida.efeito_alem_do_limite': 'Efeito além do limite de falha de taco: {valor}.',

  'evento.liquidacao.sem_chave': 'sem chave de liquidação; as entradas voltam pelo prazo',
  'evento.liquidacao.sem_revelacao': 'partida sem revelação; será reembolsada',
  'evento.liquidacao.sem_vencedor': 'partida sem vencedor; será reembolsada',
  'evento.liquidacao.id_invalido': 'identificador de partida inválido: {id}',
  'evento.liquidacao.carteira_invalida': 'carteira inválida na partida',
  'evento.liquidacao.ja_liquidada': 'já estava liquidada',
  'evento.liquidacao.falha_na_chain': 'a blockchain recusou a liquidação: {erro}',
  'evento.liquidacao.inalcancavel': 'a blockchain não respondeu; o varredor tenta de novo',

  'evento.faucet.autentique': 'Autentique a carteira antes de pedir SOL de teste.',
  'evento.faucet.so_devnet': 'O faucet só existe em devnet.',
  'evento.faucet.endereco_invalido': 'Endereço inválido.',
  'evento.faucet.aguarde': 'Aguarde {segundos}s antes de pedir de novo.',
  'evento.faucet.limite_da_rede':
    'Esta rede já pediu SOL de teste demais. Tente de novo em {segundos}s.',
  'evento.faucet.airdrop_no_limite':
    'O airdrop da rede está no limite e não há carteira de faucet configurada.',

  // Histórico (`/api/history`).
  'evento.historico.endereco_invalido': 'O endereço pedido não é uma chave Solana.',
  'evento.historico.sem_indice':
    'O servidor está sem índice de histórico; lendo direto da blockchain, que é mais lento.',
  'evento.historico.aguarde': 'Aguarde {segundos}s antes de pedir outra atualização do histórico.',
  'evento.faucet.sem_saldo': 'A carteira do faucet está sem saldo.',
  'evento.faucet.falha_ao_enviar': 'Falha ao enviar: {erro}',

  // Mint público de tacos. Domínio do evento é `mint`, não `tacos.mint`: ver
  // o comentário em `packages/protocol/src/eventos.ts`.
  'evento.mint.ja_tem': 'Esta carteira já tem um taco da coleção.',
  'evento.mint.esgotado': 'Os tacos de teste acabaram.',
  'evento.mint.pagamento_invalido': 'O pagamento na rede não bate com o preço, o destino ou a carteira.',
  'evento.mint.pagamento_nao_encontrado':
    'Ainda não achei o pagamento na rede. Espere alguns segundos e tente de novo — não será cobrado duas vezes.',
  'evento.mint.pagamento_usado': 'Esse pagamento já foi usado.',
  'evento.mint.sem_pagamento': 'Falta o pagamento.',
  'evento.mint.indisponivel': 'A cunhagem está desligada neste servidor.',
  'evento.mint.rpc': 'A rede falhou ao cunhar. Seu pagamento está guardado — tente de novo.',
  'evento.mint.rapido_demais': 'Pedidos de cunhagem demais. Espere um instante.',

  // Upgrade de taco. Domínio do evento é `upgrade`, um ponto só, como o `mint`.
  'evento.upgrade.nao_eh_seu': 'Esse taco não é desta carteira.',
  'evento.upgrade.gratuito': 'Taco gratuito não sobe de nível.',
  'evento.upgrade.orcamento': 'Este taco já gastou todos os níveis da raridade dele.',
  'evento.upgrade.nivel_maximo': 'Este atributo já está no nível máximo.',
  'evento.upgrade.teto_do_atributo': 'Este atributo já está no máximo de pontos.',
  'evento.upgrade.pagamento_invalido': 'O pagamento na rede não bate com o preço, o destino ou a carteira.',
  'evento.upgrade.pagamento_usado': 'Esse pagamento já foi usado.',
  'evento.upgrade.pagamento_nao_encontrado':
    'Ainda não achei o pagamento na rede. Espere alguns segundos e tente de novo — não será cobrado duas vezes.',
  'evento.upgrade.sem_pagamento': 'Falta o pagamento.',
  'evento.upgrade.atributo_invalido': 'Atributo desconhecido.',
  'evento.upgrade.indisponivel': 'O upgrade de tacos está desligado neste servidor.',
  'evento.upgrade.rpc': 'A rede falhou ao aplicar o nível. Seu pagamento está guardado — tente de novo.',
  'evento.upgrade.rapido_demais': 'Pedidos de upgrade demais. Espere um instante.',

  'evento.espectador.sala_nao_permite':
    'Esta mesa não aceita espectadores — quem criou decidiu assim.',
  'evento.espectador.partida_ainda_nao_comecou':
    'A partida desta mesa ainda não começou. Volte quando ela fechar.',
  'evento.espectador.joga_nesta_mesa': 'Você joga nesta mesa; entre por ela, não pela plateia.',

  'evento.chat.vazia': 'Mensagem vazia.',
  'evento.chat.longa_demais': 'A mensagem passa de {maximo} caracteres.',
  'evento.chat.rapido_demais': 'Espere um instante antes de mandar outra.',
  'evento.chat.fora_da_mesa': 'Só quem está na mesa — jogando ou assistindo — conversa nela.',
  'lobby.permitirVoz': 'Voz na mesa',
  'criar.vozExplica': 'Conversem por voz durante a partida.',
  'criar.resumo.voz': 'Voz',
  'criar.resumo.vozSim': 'ligada',
  'criar.resumo.vozNao': 'desligada',
  'taco.selecionado': 'Taco selecionado',
  'taco.apostasESaldo': 'Apostas e saldo',
  'taco.titulo': 'Taco',
  'taco.valeNaProxima': 'O taco vale a partir da próxima partida.',
  'taco.esperandoLogin': 'Entre para ver quais tacos esta carteira tem.',
  'taco.somenteOsGratuitos': 'Esta carteira só tem o taco padrão.',
  'taco.raridade.gratuito': 'Gratuito',
  'taco.raridade.common': 'Comum',
  'taco.raridade.uncommon': 'Incomum',
  'taco.raridade.rare': 'Raro',
  'taco.raridade.epic': 'Épico',
  'taco.raridade.legendary': 'Lendário',
  // A mira da mesa: o alcance da linha-guia, escolhido por quem cria e válido
  // para os dois jogadores.
  // A página pública do cofre: para onde vai o dinheiro de cada partida e em
  // que etapa está o prêmio semanal.
  'nav.cofre': 'Cofre',
  'nav.cofreTitulo': 'Para onde vai o dinheiro e como está o prêmio da semana',
  'cofre.tituloPagina': 'SOL Pool — Cofre',
  'cofre.titulo': 'O cofre',
  'cofre.subtitulo':
    'Para onde vai o dinheiro de cada partida, quanto já acumulou e em que etapa está o prêmio da semana. Tudo lido da blockchain, na hora.',
  'cofre.carregando': 'Lendo…',
  'cofre.naoLido': 'não foi possível ler',
  'cofre.poteTitulo': 'O caminho do pote',
  'cofre.poteExplica':
    'Os percentuais são lidos do contrato on-chain, não escritos nesta página. Se mudarem, esta tabela muda sozinha.',
  'cofre.poteNaoLido': 'Não foi possível ler as regras de divisão da blockchain. Tente recarregar.',
  'cofre.fatia.vencedor': 'Vencedor',
  'cofre.fatia.casa': 'Casa',
  'cofre.fatia.rank': 'Prêmio semanal',
  'cofre.fatia.tesouro': 'Tesouro',
  'cofre.vaiDireto': 'vai direto para a carteira',
  'cofre.cofresTitulo': 'Os cofres',
  'cofre.rank': 'Prêmio semanal',
  'cofre.casa': 'Casa',
  'cofre.tesouro': 'Tesouro',
  'cofre.entrou': 'Já entrou',
  'cofre.saiu': 'Já saiu',
  'cofre.conta': 'Conta on-chain',
  'cofre.semanaTitulo': 'Semana {id}',
  'cofre.acumulado': 'Acumulado',
  'cofre.pagoEm': 'Pagamento',
  'cofre.verTransacao': 'ver a transação',
  'cofre.semEntradas': 'Ninguém pontuou nesta semana ainda.',
  'cofre.fechaEm': 'Fecha em {tempo}.',
  'cofre.atrasada': 'Venceu há {tempo} e ainda não distribuiu.',
  'cofre.semanaNaoLida': 'Não foi possível ler a semana. Os cofres acima continuam válidos.',
  'cofre.historicoTitulo': 'Semanas anteriores',
  'cofre.semanaBotao': 'Semana {id}',
  'cofre.semHistorico': 'Nenhuma semana foi distribuída ainda.',
  'cofre.rodape':
    'Esta página só lê. Os saldos vêm das contas do programa na blockchain e podem ser conferidos por qualquer pessoa nos links acima.',
  'cofre.duracao.diasHoras': '{dias} d {horas} h',
  'cofre.duracao.horas': '{horas} h',
  'cofre.duracao.minutos': '{minutos} min',
  'lobby.mira': 'Mira da mesa',
  'mira.sem': 'Sem mira',
  'mira.sem.explica':
    'A tela não mostra nada da tacada: nem a linha, nem onde a branca vai tocar. Só o taco apontando.',
  'mira.padrao': 'Mira normal',
  'mira.padrao.explica':
    'A linha até a bola, o ponto de contato e um trecho curto do que acontece depois — a mesa de sempre.',
  'mira.estendida': 'Mira estendida',
  'mira.estendida.explica':
    'A trajetória inteira, com as tabelas: dá para ver se a bola entra antes de tacar.',
  'mira.custom': 'Personalizada',
  'mira.custom.curto': 'Mira de {m} m',
  'mira.custom.rotulo': 'Alcance da mira, em metros',
  'mira.custom.explica':
    'A tela mostra {m} m do caminho depois do contato. Vale para os dois jogadores.',
  'taco.attr.peso': 'Peso',
  'taco.attr.efeito': 'Efeito',
  'taco.attr.aderencia': 'Aderência',
  'taco.attr.quebra': 'Quebra',
  'taco.visor.ver': 'Ver o taco',
  'taco.gdm.ajuda': 'Gangue do Macaco: cada trait do NFT vira atributo, e quanto mais raro, mais forte.',
  'taco.visor.usar': 'Usar este taco',
  'taco.visor.emUso': 'Em uso',
  'taco.visor.fechar': 'Fechar',
  'taco.padrao': 'Padrão',
  'taco.padrao.ajuda': 'Equilibrado. É com ele que o jogo foi ajustado.',
  'taco.pesado': 'Pesado',
  'taco.pesado.ajuda': 'Quebra melhor, controla menos a branca.',
  'taco.preciso': 'Controle',
  'taco.preciso.ajuda': 'Segura a branca onde você deixou, bate mais fraco.',
  'taco.efeito': 'Efeito',
  'taco.efeito.ajuda': 'Mais controle da branca, potência mediana.',
  // O mint público: o botão do seletor, o que ele diz enquanto cunha e o
  // que aparece quando o taco chega.
  'taco.mint.botao': 'Cunhar meu taco — {preco} SOL · {restantes} de {teto}',
  'taco.mint.botaoGratis': 'Cunhar meu taco — grátis · {restantes} de {teto}',
  'taco.mint.retomar': 'Retomar cunhagem (já pago)',
  'taco.mint.esgotado': 'Tacos de teste esgotados',
  'taco.mint.indisponivel': 'Cunhagem indisponível agora',
  'taco.mint.cunhando': 'Cunhando…',
  'taco.mint.veio': 'Veio um {raridade} {arquetipo}. Já está no seletor.',
  'taco.mint.naoPode': 'Esta carteira não pode cunhar agora.',

  // Os níveis do taco NFT: um botão "+" por barra, com o preço do PRÓXIMO
  // nível daquele atributo. Apagado, a dica diz por quê — e a frase é a mesma
  // do evento que o servidor mandaria se o clique acontecesse.
  'taco.upgrade.botao': '+ {preco} SOL',
  'taco.upgrade.botaoGratis': '+ grátis',
  'taco.upgrade.botaoIndisponivel': '+',
  'taco.upgrade.subirTitulo': 'Sobe um nível deste atributo por {preco} SOL.',
  'taco.upgrade.subirTituloGratis': 'Sobe um nível deste atributo, de graça.',
  'taco.upgrade.niveis': 'níveis {gastos}/{orcamento}',
  'taco.upgrade.niveisTitulo': 'Níveis já comprados neste taco, do total que a raridade dele permite.',
  'taco.upgrade.nivelDoAtributo': '{attr} {nivel}/{max}',
  'taco.upgrade.investido': 'investido {sol} SOL',
  'taco.upgrade.investidoTitulo': 'Total já pago em níveis deste taco.',
  'taco.upgrade.subindo': 'Subindo o nível…',
  'taco.upgrade.subiu': 'Subiu um nível de {atributo}.',
  'taco.upgrade.naoPode': 'Este atributo não pode subir agora.',
  // A janela "Seus tacos" (seletor-de-tacos.ts) e o botão da carteira na barra.
  'seletor.titulo': 'Seus tacos',
  'seletor.trocar': 'Trocar taco',
  'seletor.quantos': { one: '{count} taco', other: '{count} tacos' },
  'seletor.filtros': 'Filtros dos tacos',
  'seletor.buscar': 'Buscar por nome ou número (#12)',
  'seletor.ordem': 'Ordenar',
  'seletor.ordem.raridade': 'Mais raros primeiro',
  'seletor.ordem.forca': 'Mais fortes primeiro',
  'seletor.ordem.numero': 'Por número',
  'seletor.ordem.niveis': 'Mais melhorados primeiro',
  'seletor.todas': 'Todas',
  'seletor.melhorados': 'Com melhorias',
  'seletor.anterior': 'Anterior',
  'seletor.proxima': 'Próxima',
  'seletor.pagina': 'Página {pagina} de {total}',
  'seletor.mostrando': '{inicio}–{fim} de {total}',
  'seletor.nenhum': 'Nenhum taco com esses filtros.',
  'seletor.limpar': 'Limpar filtros',
  'seletor.fechar': 'Fechar',
  'carteira.abrir': 'Carteira: saldo, apelido e sair',

  // --------------------------------------------------- a aba de cunhar (mint)
  // A aba é a página de divulgação da coleção de teste: ela abre por link
  // (`?aba=cunhar`) para quem ainda nem conectou carteira, então precisa
  // explicar o que é a rede e o que é o SOL antes de pedir qualquer coisa.
  'mint.titulo': 'Cunhe seu taco de teste',
  'mint.sub1': 'A coleção roda na devnet da Solana — é a rede de testes.',
  'mint.sub2': 'O SOL da devnet não vale dinheiro: é de graça e serve só para testar.',
  'mint.preco': '{preco} SOL por taco',
  'mint.precoDesconhecido': '—',
  'mint.contador': '{n} de {teto} cunhados',
  'mint.conecte': 'Conecte a carteira acima para cunhar o seu.',
  'mint.seuTaco': 'Seu taco',
  'mint.usarNoJogo': 'Usar no jogo',
  'mint.faltaSol': 'Falta SOL nesta carteira para pagar o taco e a taxa da rede.',
  'mint.indisponivel': 'A cunhagem está desligada neste servidor.',
  'mint.esgotado': 'A coleção de teste esgotou — não há mais tacos para cunhar.',
  'controle.titulo': 'Como tacar',
  'controle.doisGestos': 'Mirar e tacar separados',
  'controle.doisGestosAjuda': 'Arraste no pano para mirar; puxe o taco para tacar',
  'controle.arrastoUnico': 'Um arrasto só',
  'controle.arrastoUnicoAjuda': 'Arraste a partir da branca: mira e força juntas, soltar taca',
  'chat.treino': 'Modo treino: a conversa só existe em mesa com adversário.',
  'nav.novidades': 'Novidades',
  'nav.novidadesTitulo': 'O que mudou no jogo, versão a versão',
  'hud.girar': 'Deitar a tela',
  'hud.menu': 'Menu',
  'hud.efeito': 'Efeito',
  'hud.som': 'Som',
  'hud.conversa': 'Conversa',
  'hud.voz': 'Voz',
  'hud.fechar': 'Fechar',
  'menu.aba.partida': 'Partida',
  'menu.aba.taco': 'Taco',
  'menu.aba.ajustes': 'Ajustes',
  // As linhas curtas dos cartões do menu (`ui/menu-treino.ts`).
  'menu.tipo.livreSub': 'Sem regras: escolha as bolas e repita a jogada.',
  'menu.tipo.miniSub': 'Treine a disputa de quem quebra, contra a sua melhor marca.',
  'mira.sem.sub': 'Mais desafiador',
  'mira.padrao.sub': 'A linha até a bola',
  'mira.estendida.sub': 'A linha completa',
  'tema.rotulo': 'Tema dos botões',
  'tema.classico': 'Clássico',
  'tema.dourado': 'Dourado',
  'tema.neon': 'Neon',
  'tema.oceano': 'Oceano',
  'mesaVisual.rotulo': 'Cor da mesa',
  'modeloMesa.rotulo': 'Modelo da mesa',
  'modeloMesa.classica': 'Clássica',
  'modeloMesa.moderna': 'Moderna',
  'modeloMesa.luxo': 'Luxo',
  'modeloMesa.rustica': 'Rústica',
  'mesaVisual.classica': 'Clássica',
  'mesaVisual.azul': 'Azul',
  'mesaVisual.vinho': 'Vinho',
  'mesaVisual.grafite': 'Grafite',
  'hud.aviso.ajusteLigado': 'Ajuste fino ligado: a mira anda 5× mais devagar',
  'hud.aviso.ajusteDesligado': 'Ajuste fino desligado',
  'hud.aviso.travaLigada': 'Mira travada: arrastar mexe só na força',
  'hud.aviso.travaDesligada': 'Mira destravada',
  'hud.aviso.somLigado': 'Som ligado',
  'hud.aviso.somDesligado': 'Som desligado',
  'painelMesa.conversa': 'Conversa',
  'painelMesa.voz': 'Voz',
  'voz.titulo': 'Voz',
  'voz.entrar': 'Entrar na voz',
  'voz.sair': 'Sair da voz',
  'voz.microfoneCalar': 'Calar meu microfone',
  'voz.microfoneLigar': 'Abrir meu microfone',
  'voz.sozinho': 'Ninguém mais na voz por enquanto.',
  'voz.volumeDe': 'Volume de {quem}',
  'voz.semMicrofone': 'Sem microfone: você ouve, mas não fala. Libere o microfone nas permissões do site e entre de novo.',
  'voz.semSuporte': 'Este navegador está sem WebRTC, que é o que a voz usa. Costuma ser uma extensão de privacidade ou VPN desligando o recurso. Desative-a para esta página, ou use outro navegador.',
  'voz.semConexao': 'Não foi possível abrir o áudio. Sua rede pode estar bloqueando a conexão de voz. Tente de novo.',
  'voz.indisponivel': 'A voz está indisponível agora. A partida segue normalmente.',
  'evento.voz.desligada': 'Esta mesa está sem voz.',
  'evento.voz.fora_da_mesa': 'Só quem está na mesa — jogando ou assistindo — entra na voz.',
  'evento.voz.indisponivel': 'A voz está indisponível agora. A partida segue normalmente.',

  // -------------------------------------------------- fim de partida (enum)
  'fim.regras': 'regras',
  'fim.desistência': 'desistência',
  'fim.tempo': 'tempo',
  'fim.replay cheio': 'replay cheio',
  // Por que a partida acabou, numa linha que cabe na tela de resultado. Um
  // `kind` de `MatchEnding` (8-Ball) ou `SinucaEnding`; a explicação longa
  // está em `regras.fim.<kind>`, e a tela de fim aponta para `regras#fim-<kind>`.
  'fim.motivo.eight-ball-potted': 'A bola 8 foi encaçapada corretamente',
  'fim.motivo.eight-ball-early': 'A bola 8 caiu antes da hora',
  'fim.motivo.eight-ball-wrong-pocket': 'A bola 8 caiu na caçapa errada',
  'fim.motivo.eight-ball-with-foul': 'A bola 8 caiu numa tacada com falta',
  'fim.motivo.eight-ball-off-table': 'A bola 8 saiu da mesa',
  'fim.motivo.three-fouls': 'Três faltas seguidas',
  'fim.motivo.forfeit': 'Desistência',
  'fim.motivo.all-balls-potted': 'Todas as bolas encaçapadas',
  'fim.motivo.score-unreachable': 'Placar inalcançável',

  // -------------------------------------- decisões (kind + índice canônico)
  'decisao.eight-on-break': 'A 8 caiu na quebra',
  'decisao.illegal-break': 'Quebra irregular',
  'decisao.opcao.0': 'Aceitar a mesa',
  'decisao.opcao.1': 'Quebrar de novo',
  'decisao.opcao.2': 'Devolver a quebra',
  'decisao.opcao.3': 'Recolocar a 8',

  // ------------------------------------------------------------------- rede
  'rede.mainnet': 'MAINNET',
  'rede.real': 'Dinheiro real. Cada mesa gasta SOL de verdade.',
  'rede.local': 'Rede local na sua máquina. Nada aqui existe fora dela.',
  'rede.teste': 'Rede de teste. O SOL usado aqui não tem valor nenhum.',
  'rede.conectado': 'conectado',
  'rede.conectando': 'conectando…',
  'rede.offline': 'offline',
  'rede.escrow': 'escrow on-chain',
  'rede.contratoTitulo': 'Contrato que guarda os depósitos',
  'rede.contrato': 'contrato {endereco}',
  'rede.rpcPublico': 'RPC público',
  'rede.rpcPublicoTitulo':
    'Configure VITE_RPC_URL com um RPC dedicado (Helius, QuickNode, Triton). O endpoint público da Solana recusa chamadas com 403 quando passa do limite.',

  // -------------------------------------------------------------- cabeçalho
  'cabecalho.subtitulo': '8-Ball 1v1 com aposta em {simbolo}. Salas com escrow on-chain.',

  // ---------------------------------------------------------------- entrada
  'entrada.aguardandoCarteira': 'Aguardando a carteira…',
  // A tela de entrada em passos (`ui/entrada.ts`).
  'entrada.titulo': 'Entre para jogar',
  'entrada.passos': 'Passos para entrar',
  'entrada.passo1': 'Tenha uma carteira Solana',
  'entrada.passo1Sub': 'Phantom, Solflare ou Backpack.',
  'entrada.passo2': 'Conecte a carteira',
  'entrada.passo2Sub': 'Escolha a sua na lista.',
  'entrada.passo3': 'Assine para entrar',
  'entrada.passo3Sub': 'É grátis e não move SOL.',
  'entrada.assinarExplica': 'Assinar só prova que a carteira é sua: não custa nada e não autoriza nenhuma transação.',
  'entrada.celularExplica': 'No celular, o jogo abre dentro do app da sua carteira. Toque na sua:',
  'entrada.abrirNoApp': 'Abrir no app {carteira}',
  'entrada.celularSemApp': 'Ainda não tem? O botão leva à página da carteira, que manda para a loja.',
  'entrada.computadorExplica': 'Nenhuma carteira encontrada neste navegador. Instale uma extensão:',
  'entrada.instalar': 'Instalar {carteira}',
  'entrada.jaInstalei': 'Já instalei, recarregar',
  'entrada.treinarSemCarteira': 'Só quero conhecer: treinar sem carteira',
  'entrada.reconectando': 'Reconectando sua carteira…',
  'entrada.assinarLogin': 'Assinar login',
  'entrada.desconectar': 'Desconectar carteira',
  'entrada.conectadoCom':
    'Conectado com {carteira}. A assinatura de login é gratuita e não move fundos. Depósitos são transações separadas, que você aprova uma a uma.',
  'entrada.jogaDeVerdade': 'Você vai jogar com {simbolo} de verdade na rede {rede}.',
  'entrada.semCarteira':
    'Nenhuma carteira Solana encontrada neste navegador. Instale uma — {phantom}, {solflare}, {backpack} e outras servem — e ela aparecerá aqui sozinha.',
  'entrada.recusadas':
    'Não utilizáveis aqui: {lista}. Esta mesa exige carteira que assine a transação sem transmiti-la, para a partida nunca sair na rede errada.',

  // ------------------------------------------------------- dinheiro em jogo
  'emJogo.titulo': 'Dinheiro em jogo',
  'emJogo.esperando': 'esperando adversário',
  'emJogo.prazoEm': 'em disputa · prazo em {min} min',
  'emJogo.vencidoHa': 'prazo vencido há {min} min',
  'emJogo.destravar': 'Destravar e receber de volta',
  'emJogo.nota':
    'Lido do contrato, não do nosso servidor — por isso aparece mesmo se o servidor estiver fora do ar. Vencido o prazo, qualquer pessoa pode destravar, e o contrato só devolve aos dois jogadores.',
  'emJogo.devolvido': 'Depósito devolvido aos dois jogadores.',

  // ------------------------------------------------------------- minha mesa
  'minhaMesa.aberta':
    'Sua mesa está aberta e o depósito está travado no contrato. Sai da lista em {contador} se ninguém entrar — o dinheiro continua seu, basta cancelar.',
  'minhaMesa.fechada': 'Mesa fechada com {adversario}. Os dois depósitos estão no contrato.',
  'minhaMesa.mesa': 'Mesa',
  'minhaMesa.modalidade': 'Modalidade',
  'minhaMesa.entrada': 'Entrada',
  'minhaMesa.pote': 'Pote',
  'minhaMesa.vencedorLeva': 'Vencedor leva',
  'minhaMesa.adversario': 'Adversário',
  'minhaMesa.cancelando': 'Cancelando…',
  'minhaMesa.espectadores': 'Espectadores',
  'minhaMesa.espectadoresSim': 'Permitidos',
  'minhaMesa.espectadoresNao': 'Só vocês dois',
  'minhaMesa.senha': 'Senha',
  'minhaMesa.sim': 'Sim',
  'minhaMesa.nao': 'Não',
  'minhaMesa.proximoPasso': 'O que acontece agora',
  'minhaMesa.proximoPasso.aguardando':
    'Quando alguém entrar e depositar, a partida abre sozinha e você é levado à mesa. Enquanto isso, mande o convite.',
  'minhaMesa.proximoPasso.fechada':
    'Os dois depósitos estão no contrato. Entre na mesa: a quebra é combinada quando os dois estiverem lá.',
  'minhaMesa.cancelar': 'Cancelar e receber de volta',
  'minhaMesa.entrar': 'Entrar na mesa',
  'minhaMesa.reembolso':
    'Se a partida não começar, os dois depósitos voltam sozinhos em {contador}.',
  'minhaMesa.reembolsoVencido':
    'O prazo da mesa venceu: o servidor está devolvendo os depósitos. Pode levar alguns minutos.',
  'minhaMesa.notaCancelar':
    'Cancelar é uma transação: a carteira vai pedir aprovação e o depósito volta na hora.',
  'minhaMesa.notaFechada':
    'Os dois depósitos estão no contrato. A partida começa assim que os dois entrarem na mesa.',

  // ------------------------------------------------------------------ lobby
  'lobby.entrar': 'Entrar',
  'lobby.sair': 'Sair',
  'lobby.criarTitulo': 'Crie ou entre em uma mesa',
  'lobby.criarMesa': 'Criar mesa',
  'lobby.modalidade': 'Modalidade',
  'lobby.notaCriar':
    'Criar ou entrar numa mesa é uma transação real na {rede}. O depósito fica no contrato até a partida terminar.',
  'lobby.mesasAbertas': 'Mesas abertas',
  'lobby.nenhumaMesa': 'Nenhuma mesa aberta. Crie a primeira.',
  'lobby.treinar': 'Treinar',
  'lobby.treinarEnquantoEspera': 'Ou treine enquanto espera',
  'lobby.nenhumTorneio': 'Nenhum torneio aberto. Crie o primeiro e chame a galera.',
  'lobby.nenhumaSerie': 'Nenhuma melhor de 3 aberta. Crie a primeira.',
  'lobby.mesas': 'Mesas',
  'lobby.assistir': 'Assistir',
  'lobby.permitirEspectadores': 'Permitir espectadores',
  'lobby.emDisputa': 'em disputa',
  'lobby.plateiaAberta': 'plateia aberta',
  'lobby.suasPartidas': 'Suas partidas',
  'lobby.notaHistorico':
    'Lido direto da blockchain. Cada partida é reproduzida aqui no seu navegador: o selo confirma que os bytes gravados batem com o hash da liquidação e chegam a um vencedor.',
  'lobby.solDeTesteRecebido': 'SOL de teste recebido.',

  // ------------------------------------------------------ criar mesa (modal)
  // Duas etapas: escolher (com a prévia ao vivo) e confirmar. Os números da
  // prévia vêm do contrato e do protocolo — nenhum é digitado aqui.
  'criar.titulo': 'Criar mesa',
  'criar.etapa': 'Etapa {n} de 2',
  'criar.fechar': 'Fechar',
  'criar.entrada': 'Valor da entrada',
  'criar.outroValor': 'Outro valor',
  'criar.faixa': 'Entre {min} e {max} {simbolo}',
  'criar.faixaDesconhecida': 'Lendo os limites do contrato…',
  'criar.saldo': 'Saldo: {saldo} {simbolo}',
  'criar.erro.invalida': 'Valor inválido. Use números, com ponto ou vírgula.',
  'criar.erro.abaixoDoMinimo': 'Abaixo do mínimo do contrato: {min} {simbolo}.',
  'criar.erro.acimaDoMaximo': 'Acima do máximo do contrato: {max} {simbolo}.',
  'criar.erro.excedeSaldo':
    'Passa do saldo: entrada mais taxas precisam de {necessario} {simbolo}, e a carteira tem {tem}.',
  'criar.semSaldoUnica':
    'Sua carteira está sem {simbolo}. A partida única pede um depósito; melhor de 3 e torneio são amistosos e dá para criar sem saldo.',
  'criar.verRegras': 'ver regras ↗',
  'criar.nome': 'Nome da mesa',
  'criar.nomeOpcional': 'opcional · até {max} caracteres',
  'criar.placeholderNome': 'Ex.: Mesa do Zé',
  'criar.espectadoresExplica': 'Qualquer pessoa pode assistir à partida.',
  'criar.previa': 'Como fica',
  'criar.pote': 'Pote total (2 × entrada)',
  'criar.vencedorLeva': 'Vencedor leva ({pct})',
  'criar.corteCasa': 'Casa ({pct})',
  'criar.corteRank': 'Cofre de rank ({pct})',
  'criar.corteTesouraria': 'Tesouraria ({pct})',
  'criar.percentuaisLendo': 'Lendo os percentuais do contrato…',
  'criar.percentuaisDaChain': 'Percentuais lidos do contrato, não digitados aqui.',
  'criar.prazos': 'Prazos',
  'criar.prazoTacada': 'Por tacada',
  'criar.prazoRevelacao': 'Revelar a quebra',
  'criar.prazoDuracao': 'Duração máxima',
  'criar.continuar': 'Continuar',
  'criar.voltar': 'Voltar',
  'criar.cancelar': 'Cancelar',
  'criar.confirmar': 'Confirmar a mesa',
  'criar.resumo.entrada': 'Entrada',
  'criar.resumo.pote': 'Pote',
  'criar.resumo.seVencer': 'Se vencer, recebe',
  'criar.resumo.modalidade': 'Modalidade',
  'criar.resumo.nome': 'Nome',
  'criar.resumo.semNome': 'sem nome',
  'criar.resumo.espectadores': 'Espectadores',
  'criar.resumo.espectadoresSim': 'sim, plateia aberta',
  'criar.resumo.espectadoresNao': 'não, só os dois jogadores',
  'criar.resumo.prazoTacada': 'Prazo por tacada',
  'criar.notaConfirmar':
    'Ao confirmar, a carteira pede a assinatura de uma transação real na {rede}. O depósito fica no contrato até a partida terminar, e volta se ninguém entrar.',
  'criar.depositarECriar': 'Depositar e criar',
  'criar.senha': 'Senha da mesa',
  'criar.senhaOpcional': 'opcional · {min} a {max} caracteres',
  'criar.senhaExplica': 'Só entra ou assiste quem souber a senha. Ela não é salva no sistema.',
  'criar.placeholderSenha': 'Sem senha',
  'criar.mostrarSenha': 'Mostrar senha',
  'criar.ocultarSenha': 'Ocultar senha',
  'criar.erro.senhaCurta': 'Senha curta demais: no mínimo {min} caracteres.',
  'criar.erro.senhaLonga': 'Senha longa demais: no máximo {max} caracteres.',
  'criar.resumo.senha': 'Senha',
  'criar.resumo.senhaSim': 'sim, só com a senha',
  'criar.resumo.senhaNao': 'não, mesa aberta',

  // -------------------------------------------------------- abas do lobby
  'abas.rotulo': 'Seções do lobby',
  'abas.mesas': 'Mesas',
  'abas.emjogo': 'Em jogo',
  'abas.partidas': 'Suas partidas',
  'abas.ranking': 'Ranking',
  'abas.cunhar': 'Cunhar',
  'emJogo.vazio': 'Nenhum depósito seu está preso no contrato agora.',

  // ------------------------------------------------------ filtros das mesas
  'filtro.rotulo': 'Filtrar mesas',
  'filtro.abertas': 'Abertas',
  'filtro.disputa': 'Em disputa (plateia aberta)',
  'filtro.todas': 'Todas',
  'filtro.modoTodas': 'Todas as modalidades',
  'filtro.nenhuma': 'Nenhuma mesa com este filtro.',

  // ------------------------------------------------- detalhe da sala (modal)
  'sala.detalhes': 'Detalhes da mesa {nome}',
  'sala.subtitulo': 'Mesa',
  'sala.estado': 'Estado',
  'sala.estado.waiting': 'Aberta, esperando adversário',
  'sala.estado.committed': 'Fechada, os dois depositaram',
  'sala.estado.playing': 'Em disputa',
  'sala.estado.settled': 'Encerrada, aguardando liquidação',
  'sala.criador': 'Criador',
  'sala.oponente': 'Oponente',
  'sala.voce': 'você',
  'sala.saiDaListaEm': 'Sai da lista em',
  'sala.prazoVencido': 'prazo vencido',
  'sala.prazoContrato': 'Prazo do contrato',
  'sala.prazoContratoValor': '{min} min depois de fechar',
  'sala.entrarEDepositar': 'Entrar e depositar',
  'sala.notaEntrar':
    'Entrar deposita {valor} {simbolo} no contrato, com a assinatura da sua carteira. Os dois depósitos ficam lá até a partida terminar.',
  'sala.semAcao': 'Esta mesa está fechada para quem está de fora.',
  'sala.sumiu': 'Esta mesa não está mais na lista.',

  // ------------------------------------------------------------- convite
  'convite.entrarComCodigo': 'Entrar com código',
  'convite.explicaCampo': 'Recebeu um código de 6 caracteres? Digite aqui para abrir a mesa.',
  'convite.placeholder': 'K7M-2PX',
  'convite.abrir': 'Abrir mesa',
  'convite.procurando': 'Procurando…',
  'convite.titulo': 'Convide alguém',
  'convite.codigo': 'Código da mesa',
  'convite.explica': 'Quem tiver o código, ou o link, encontra esta mesa no lobby.',
  'convite.copiarLink': 'Copiar convite',
  'convite.copiarCodigo': 'Copiar código',
  'convite.copiado': 'Copiado!',
  'convite.falhouCopiar': 'Não deu para copiar. Selecione e copie à mão.',
  'convite.linkComSenha': 'O link copiado leva a senha junto: mande só para quem deve entrar.',
  'convite.linkSemSenha':
    'A senha desta mesa não está no link (ela não fica guardada): passe-a por fora.',
  'sala.protegida': 'Mesa com senha',
  'sala.senha': 'Senha da mesa',
  'sala.senhaExplica': 'Esta mesa tem senha. Peça a quem criou e digite abaixo.',
  'sala.senhaVazia': 'Digite a senha para continuar.',

  // ----------------------------------------------------------------- faucet
  'faucet.semSol': 'Esta carteira não tem SOL na {rede}.',
  'faucet.semSolDevnet':
    'Sem SOL na {rede}. O SOL de mainnet não aparece aqui — são redes separadas.',
  'faucet.precisaMais': 'Precisa de mais SOL de teste?',
  'faucet.pedindo': 'Pedindo…',
  'faucet.pedir': 'Pedir SOL de teste',

  // ------------------------------------------------------------------ motor
  'motor.indisponivel': 'Apostas indisponíveis neste navegador',
  'motor.explicacao':
    'A física precisa dar exatamente o mesmo resultado em todo lugar — é isso que faz o replay de uma partida provar quem ganhou. Onde ainda não conferimos, preferimos não deixar dinheiro entrar.',
  'motor.verificar': 'Rodar a verificação aqui',

  // ------------------------------------------------------------ retrospecto
  'retrospecto.titulo': 'Seu retrospecto',
  'retrospecto.vitorias': { one: 'vitória', other: 'vitórias' },
  'retrospecto.derrotas': { one: 'derrota', other: 'derrotas' },
  'retrospecto.aproveitamento': 'aproveit.',
  'retrospecto.saldo': 'Saldo em partidas',

  // ------------------------------------------------------- ranking do cofre
  'ranking.titulo': 'Ranking do cofre',
  'ranking.cofre': 'Cofre desta semana',
  'ranking.saldoAtrasado': 'atualiza com a blockchain a cada 5 min',
  'ranking.semana': 'Semana {id}',
  'ranking.emDisputa': 'em disputa',
  'ranking.fechaEm': 'fecha em {tempo}',
  'ranking.distribuidoEm': 'distribuída em {data}',
  'ranking.total': 'Total distribuído',
  'ranking.colPosicao': '#',
  'ranking.colJogador': 'Jogador',
  'ranking.colPontos': 'Pontos',
  'ranking.colPremio': 'Prêmio',
  'ranking.voce': 'você',
  'ranking.suaPosicao': 'Sua posição: {posicao}ª nesta semana',
  'ranking.semPontuar': 'Você ainda não pontuou nesta semana.',
  'ranking.vazio': 'Ninguém pontuou nesta semana ainda.',
  'ranking.carregando': 'Lendo o ranking…',
  'ranking.indisponivel': 'Ranking indisponível neste servidor.',
  'ranking.erroCarregar': 'Não foi possível carregar o ranking.',
  'ranking.semanaDesconhecida': 'Essa semana não existe.',
  'ranking.mesSemSemana': 'Nenhuma semana registrada neste mês.',
  'ranking.mesAnterior': 'Mês anterior',
  'ranking.mesSeguinte': 'Próximo mês',
  'ranking.verNaChain': 'ver distribuição na chain',
  'ranking.voltar': 'Voltar',
  'ranking.perfilGraficoAlt': 'Gráfico de pontos de {jogador} por semana',
  'ranking.perfilGraficoTitulo': 'Pontos por dia · últimos {dias} dias',
  'ranking.perfilMelhorDia': 'melhor dia: {pontos} pts',
  'ranking.perfilBarraDica': '{data} · {partidas} partida(s) · {pontos} pts',
  'ranking.perfilResumoLinha': '{partidas} partida(s) · {adversarios} adversário(s) · {pontos} pts{posicao}',
  'ranking.perfilPartidas': 'Partidas',
  'ranking.perfilSemPartidas': 'Este jogador ainda não tem partidas liquidadas.',
  'ranking.perfilPartidasErro': 'Não foi possível carregar as partidas.',
  'ranking.perfilVitoria': 'vitória',
  'ranking.perfilDerrota': 'derrota',
  'ranking.perfilPartidaLinha': '{premio} · {pontos} pts',
  'ranking.perfilPagina': 'Página {pagina} de {total}',

  // -------------------------------------------------------------- histórico
  'historico.vazio': 'Nenhuma partida liquidada ainda.',
  'historico.vitoria': 'Vitória',
  'historico.derrota': 'Derrota',
  'historico.poteTitulo': 'Pote da partida: {valor}',
  'historico.pote': '{valor} (pote)',
  'historico.noPote': '{valor} no pote',
  'historico.reproduz': 'O replay gravado reproduz este resultado.',
  'historico.selo.confere': '✓ replay confere',
  'historico.selo.divergiu': '⚠ replay diverge',
  'historico.selo.erro': '· não verificável',
  'historico.selo.indisponivel': '· replay não arquivado',
  'historico.versaoTitulo':
    'Formato do replay e versão da física com que a partida foi jogada. Uma partida só reproduz igual na física que a gerou.',
  'historico.versao': 'fmt v{formato} · fis v{fisica}',
  'historico.assistir': '▶ assistir a partida',
  'historico.motivo.naoArquivado':
    'os bytes deste replay não estão arquivados. O compromisso on-chain continua válido: qualquer cópia que apareça pode ser conferida contra ele.',
  'historico.motivo.hashDiverge': 'os bytes servidos não batem com o hash gravado na liquidação',
  'historico.motivo.semVencedor': 'o replay não chega a um vencedor',
  'historico.motivo.seed': 'o seed da quebra não veio dos nonces gravados',
  'historico.motivo.vencedorDiverge': 'quem recebeu o pote não é quem o replay diz que venceu',
  'historico.motivo.formatoAntigo':
    'Gravada com uma versão anterior do formato. Continua auditável com o código daquela época.',
  'historico.motivo.fisicaAntiga':
    'Gravada com uma versão anterior da física. Continua auditável com a engine daquela época.',

  // -------------------------------------------------------------- navegação
  'nav.lobby': 'Lobby',
  'nav.lobbyTitulo': 'Mesas abertas e seu histórico',
  'nav.treino': 'Treino',
  'nav.treinoTitulo': 'Jogar sozinho, sem aposta e sem carteira',
  'nav.determinismo': 'Determinismo',
  'nav.determinismoTitulo': 'Prova de que a física roda igual em qualquer navegador',
  'nav.regras': 'Regras',
  'nav.regrasTitulo': 'Pelo que você joga: regras, faltas, prazos e limites',
  'nav.discord': 'Discord',
  'nav.conectarCarteira': 'Conectar carteira',
  'nav.escolhaCarteira': 'Escolha a carteira',
  'nav.discordTitulo': 'Entrar no servidor: ideias, problemas e quem está jogando',

  // ----------------------------------------------------------------- replay
  'replay.tituloPagina': 'SOL Pool — Assistir replay',
  'replay.cabecalho': 'Replay verificado',
  'replay.intro':
    'Os bytes são conferidos contra o hash gravado na blockchain antes de qualquer coisa ser desenhada. Partida que não confere não é reproduzida.',
  'replay.tocar': 'Tocar',
  'replay.pausar': 'Pausar',
  'replay.fim': 'Fim',
  'replay.proximaTacada': 'Próxima tacada',
  'replay.simulacaoTitulo': 'Simulação, tacada a tacada',
  'replay.col.jogador': 'jog',
  'replay.col.angulo': 'ângulo',
  'replay.col.forca': 'força',
  'replay.col.efeito': 'efeito',
  'replay.col.hash': 'hash do estado',
  'replay.col.naMesa': 'na mesa',
  'replay.col.julgamento': 'julgamento',
  'replay.toqueParaComecar': 'Toque para começar.',
  'replay.notaHash':
    'O <span class="font-mono">hash do estado</span> é o mesmo que cliente e servidor trocam durante a partida para detectar divergência. Recalcule-o na sua máquina a partir destes bytes: se bater, a física rodou igual aí.',
  'replay.registroOnChain': 'Registro on-chain',
  'replay.status.lendoRegistro': 'Lendo o registro on-chain…',
  'replay.status.buscandoBytes': 'Buscando os bytes do replay…',
  'replay.status.verificando': 'Verificando compatibilidade e reproduzindo a partida…',
  'replay.erro.partidaInvalida': 'Partida inválida',
  'replay.erro.partidaInvalidaDetalhe': 'Use ?match=<id de 32 caracteres hex>.',
  'replay.erro.naoEncontrada': 'Partida não encontrada',
  'replay.erro.naoEncontradaDetalhe': 'Não existe registro liquidado com este id.',
  'replay.erro.naoArquivado': 'Replay não arquivado',
  'replay.erro.naoArquivadoDetalhe':
    'O compromisso on-chain continua válido — só não temos os bytes. Quem tiver uma cópia pode publicá-la na chain, e ela passa a valer para todo mundo.',
  'replay.erro.bytesNaoConferem': 'Os bytes não conferem',
  'replay.erro.bytesNaoConferemDetalhe':
    'O SHA-256 do replay recebido não bate com o gravado na liquidação. Isto não é uma partida antiga: é um arquivo errado ou adulterado.',
  'replay.erro.incompleto': 'Replay incompleto',
  'replay.erro.incompletoDetalhe': 'A reexecução não chega a um vencedor.',
  'replay.erro.vencedorDivergente': 'Vencedor divergente',
  'replay.erro.vencedorDivergenteDetalhe':
    'Quem recebeu o pote não é quem o replay diz que venceu. Não vamos reproduzir isto como se fosse a partida.',
  'replay.verificado': 'Verificado.',
  'replay.verificadoDetalhe':
    'Os bytes conferem com o hash gravado on-chain, e a reexecução confirma o vencedor pago.',
  'replay.semVideo': 'Sem vídeo.',
  'replay.ficha.partida': 'partida',
  'replay.ficha.vencedor': 'vencedor',
  'replay.ficha.perdedor': 'perdedor',
  'replay.ficha.pote': 'pote',
  'replay.ficha.liquidadaEm': 'liquidada em',
  'replay.ficha.replay': 'replay',
  'replay.ficha.nonceCriador': 'nonce criador',
  'replay.ficha.nonceOponente': 'nonce oponente',
  'replay.ficha.seed': 'seed da quebra',
  'replay.ficha.formato': 'formato',
  'replay.ficha.fisica': 'física',
  'replay.ficha.modalidade': 'modalidade',
  'replay.bytes': '{n} bytes',
  'replay.info.tacada': 'tacada {atual} de {total}',
  'replay.info.vez': 'vez do jogador {n}',
  'replay.info.vencedorGravado': 'vencedor gravado: {quem}',
  'replay.criador': 'criador',
  'replay.oponente': 'oponente',
  'replay.vezPassa': 'vez passa',
  'replay.cacapaDeclarada': '(caçapa {n})',
  'replay.partidaEncerrada': '· partida encerrada',
  'replay.engineIncompativel':
    'Esta partida foi jogada na física v{doReplay} e este código roda a v{daEngine}. O replay continua válido e verificável — o que falta aqui é a física que o produziu.',

  // ----------------------------------------------------------- determinismo
  'determinismo.tituloPagina': 'Determinismo da engine',
  'determinismo.voltar': '← Voltar ao site',
  'determinismo.sobrancelha': 'verificação de determinismo · física v{versao}',
  'determinismo.legendaCima': 'em cima — o que ESTE navegador calculou',
  'determinismo.legendaBaixo': 'embaixo — o que está gravado como referência',
  'determinismo.concorda': 'Este navegador concorda com o servidor.',
  'determinismo.divergiu': 'Divergiu em {falhas} de {total} partidas.',
  'determinismo.explicaOk':
    'As {total} partidas de referência reproduziram exatamente os hashes gravados. É disto que dependem a predição da tacada no cliente e a auditoria pública do replay: bastaria um hash divergir para nenhuma das duas provar nada.',
  'determinismo.explicaFalha':
    'Enquanto isto durar, a predição da tacada e a auditoria do replay não valem neste navegador — a mesa daqui evolui diferente da do servidor. As colunas marcadas acima mostram em que posição os digests se separaram; a lista abaixo, quais partidas causaram isso.',
  'determinismo.partidas': 'partidas',
  'determinismo.conferem': 'conferem',
  'determinismo.tempo': 'tempo',
  'determinismo.digest': 'digest',
  'determinismo.conferidas': 'conferidas',
  'determinismo.andamento': 'andamento',
  'determinismo.trilhaNota': 'um segmento por partida — passe o ponteiro para ver o nome',
  'determinismo.trilhaAria': '{ok} de {total} partidas conferem',
  'determinismo.divergiuSufixo': ' — divergiu',
  'determinismo.tabelaTitulo': 'partidas que divergiram',
  'determinismo.colPartida': 'partida',
  'determinismo.colGravado': 'gravado',
  'determinismo.colObtido': 'obtido aqui',
  'determinismo.rodape':
    'Para conferir por conta própria: <code>bun run determinism</code> abre esta página em cada navegador instalado, num perfil limpo, e compara os vereditos. O resultado também fica em <code>window.__determinism</code>.',
  'determinismo.conferindo': 'Conferindo…',
  'determinismo.esperaExplica':
    'Este navegador está reproduzindo as partidas de referência e comparando cada hash com o que está gravado. São mais de trinta segundos de conta — a página fica respondendo enquanto isso, porque o trabalho acontece numa thread separada.',
  'determinismo.semWorker': 'Este navegador não tem Web Worker.',
  'determinismo.semWorkerExplica':
    'A bateria só roda travando a página, e por mais de trinta segundos. Abra num navegador com suporte a worker, ou rode <code>bun test</code> no servidor.',
  'determinismo.naoRodou': 'A bateria não chegou a rodar.',
  'determinismo.naoRodouExplica':
    'O worker falhou ao carregar. Isso NÃO é uma divergência de determinismo — é a ferramenta que quebrou, e o veredito continua desconhecido.',
  'determinismo.semReferencia': '(sem referência)',

  // ------------------------------------------------------------------ admin
  'admin.tituloPagina': 'SOL Pool — Admin',
  'admin.titulo': 'Administração',
  'admin.faixa': 'Painel de administração — leitura direta da blockchain.',
  'admin.atualizadoAs': 'atualizado {hora}',
  'admin.carregando': 'carregando…',
  'admin.atualizar': 'Atualizar',
  'admin.lendo': 'Lendo a blockchain…',
  'admin.configuracao': 'Configuração',
  'admin.configNaoEncontrado': 'Config não encontrado on-chain.',
  'admin.programa': 'Programa',
  'admin.autoridade': 'Autoridade',
  'admin.referee': 'Referee',
  'admin.entrada': 'Entrada',
  'admin.divisao': 'Divisão',
  'admin.divisaoValor': 'vencedor {vencedor} · casa {casa} · protocolo {protocolo}',
  'admin.estado': 'Estado',
  'admin.pausado': 'PAUSADO',
  'admin.operando': 'operando',
  'admin.cofres': 'Cofres',
  'admin.casa': 'Casa',
  'admin.protocolo': 'Protocolo',
  'admin.sacado': 'sacado',
  'admin.queimado': 'queimado',
  'admin.naoCriado': 'não criado',
  'admin.entrou': 'entrou',
  'admin.partidas': { one: 'partida', other: 'partidas' },
  'admin.mesas': 'Mesas',
  'admin.travados': '{valor} SOL travados',
  'admin.nenhumaMesa': 'Nenhuma mesa aberta.',
  'admin.col.conta': 'conta',
  'admin.col.estado': 'estado',
  'admin.col.entrada': 'entrada',
  'admin.col.pote': 'pote',
  'admin.col.noCofre': 'no cofre',
  'admin.col.criador': 'criador',
  'admin.col.oponente': 'oponente',
  'admin.col.prazo': 'prazo',
  'admin.aguardando': 'aguardando',
  'admin.comprometida': 'comprometida',
  'admin.destravar': 'Destravar',
  'admin.vencidaHa': 'vencida há {tempo}',
  'admin.semOponente': 'Mesa sem oponente: quem cancela é o criador, pelo próprio jogo.',
  'admin.destravada': 'Destravada. Os depósitos voltaram para os dois jogadores. ({assinatura})',
  'admin.recusou': 'Você recusou na carteira.',
  'admin.conecteNaBarra': 'Conecte a carteira de admin na barra do topo.',
  'admin.falhaLeitura': 'Falha ao ler a blockchain: {erro}',
  'admin.rodape':
    'Pausar, trocar o referee e sacar do cofre exigem a chave de autoridade, que não deve viver num navegador. Use <code>bun scripts/admin.ts</code>.',

  // ------------------------------------------------------ admin: coleções
  'admin.colecoes.abaMesas': 'Mesas e cofres',
  'admin.colecoes.aba': 'Coleções',
  'admin.colecoes.titulo': 'Coleções parceiras',
  'admin.colecoes.entrar': 'Entrar como admin',
  'admin.colecoes.entrarExplica':
    'A aba de coleções pede uma assinatura da carteira de admin. A sessão dura 15 minutos, só serve para ler e montar rascunho, e some ao recarregar a página.',
  'admin.colecoes.voceVaiAssinar': 'Você vai assinar:',
  'admin.colecoes.nenhuma': 'Nenhuma coleção cadastrada.',
  'admin.colecoes.abrir': 'Abrir',
  'admin.colecoes.voltar': '← Coleções',
  'admin.colecoes.col.nome': 'nome',
  'admin.colecoes.col.rede': 'rede',
  'admin.colecoes.col.estado': 'estado',
  'admin.colecoes.col.cadastro': 'cadastro',
  'admin.colecoes.col.distribuicao': 'distribuição',
  'admin.colecoes.estado.rascunho': 'rascunho',
  'admin.colecoes.estado.em_analise': 'em análise',
  'admin.colecoes.estado.aprovada': 'aprovada',
  'admin.colecoes.estado.recusada': 'recusada',
  'admin.colecoes.estado.suspensa': 'suspensa',
  'admin.colecoes.emAnaliseSelo': 'em análise: v{versao}',
  'admin.colecoes.cadastradaAdmin': 'cadastrada pelo admin',
  'admin.colecoes.cadastradaAutoridade': 'cadastrada pelo dono',
  'admin.colecoes.alfabetos': '⚠ letras de alfabetos misturados',
  'admin.colecoes.alfabetosDica': 'Latim junto com cirílico ou grego na mesma palavra: é assim que um nome falso imita um verdadeiro.',
  'admin.colecoes.cadastrar.titulo': 'Cadastrar em nome do dono',
  'admin.colecoes.cadastrar.explica':
    'O endereço da coleção (a NFT de coleção verificada), a rede e a carteira do dono. A coleção nasce como rascunho; nada vai para o jogo sem aprovação.',
  'admin.colecoes.cadastrar.endereco': 'Endereço da coleção',
  'admin.colecoes.cadastrar.rede': 'Rede',
  'admin.colecoes.cadastrar.dono': 'Carteira do dono',
  'admin.colecoes.cadastrar.botao': 'Assinar e cadastrar',
  'admin.colecoes.holders': 'holders: {holders}',
  'admin.colecoes.modeloBadge': 'modelo: {modelo}',
  'admin.colecoes.dono': 'dono {dono}',
  'admin.colecoes.motivo': 'Motivo registrado: {motivo}',
  'admin.colecoes.avisoPiso': 'Todo taco desta coleção fica melhor que o gratuito em tudo. Isto mexe no rank e no cofre.',
  'admin.colecoes.avisoOrdem': 'A numeração veio da ordem dos mints (alguma NFT não tem "#número" no nome); confira antes de aprovar.',
  'admin.colecoes.versao.aprovada': 'aprovada',
  'admin.colecoes.versao.emAnalise': 'em análise',
  'admin.colecoes.versao.rascunho': 'rascunho',
  'admin.colecoes.versao.tacos': '{total} tacos, {comArte} com arte',
  'admin.colecoes.versao.ver': 'Ver esta',
  'admin.colecoes.versao.naTela': 'na tela',
  'admin.colecoes.acao.enviar': 'Enviar v{versao} para análise',
  'admin.colecoes.acao.aprovar': 'Assinar e aprovar v{versao}',
  'admin.colecoes.acao.recusar': 'Assinar e recusar',
  'admin.colecoes.acao.suspender': 'Assinar e suspender',
  'admin.colecoes.acao.reativar': 'Assinar e reativar',
  'admin.colecoes.acao.motivo': 'Motivo (recusar, suspender, reativar)',
  'admin.colecoes.motivoRuim': 'Escreva um motivo de uma linha, até 200 caracteres, sem espaços duplos.',
  'admin.colecoes.soLeitura': 'Coleção cadastrada pelo dono: a tabela é dele. Aqui só se analisa.',
  'admin.colecoes.tabela': 'Tabela NFT → taco',
  'admin.colecoes.modelo': 'Modelo de pontos',
  'admin.colecoes.salvarEdicoes': { one: 'Salvar rascunho ({count} alteração)', other: 'Salvar rascunho ({count} alterações)' },
  'admin.colecoes.edicoes.descartar': 'Descartar',
  'admin.colecoes.edicoes.explica': 'Salvar cria uma versão nova do rascunho.',
  'admin.colecoes.selecao.quantas': { one: '{count} selecionada', other: '{count} selecionadas' },
  'admin.colecoes.selecao.raridade': 'Raridade',
  'admin.colecoes.selecao.arquetipo': 'Arquétipo',
  'admin.colecoes.selecao.limpar': 'Limpar seleção',
  'admin.colecoes.selecao.visiveis': { one: 'Selecionar a visível', other: 'Selecionar as {count} visíveis' },
  'admin.colecoes.selecao.marcar': 'Selecionar #{numero}',
  'admin.colecoes.raridadeCurta.common': 'Com.',
  'admin.colecoes.raridadeCurta.uncommon': 'Inc.',
  'admin.colecoes.raridadeCurta.rare': 'Raro',
  'admin.colecoes.raridadeCurta.epic': 'Épico',
  'admin.colecoes.raridadeCurta.legendary': 'Lend.',
  'admin.colecoes.vista.rotulo': 'Ver como',
  'admin.colecoes.vista.cards': 'Cartões',
  'admin.colecoes.vista.tabela': 'Tabela',
  'admin.colecoes.modeloOpcao.ruleset-v3': 'ruleset-v3 — o orçamento dos nossos tacos',
  'admin.colecoes.modeloOpcao.piso-do-gratuito': 'piso-do-gratuito — o grátis + a raridade',
  'admin.colecoes.modeloOpcao.por-traits': 'por-traits (padrão) — os pontos vêm dos traits da NFT',
  'admin.colecoes.modeloSobre.ruleset-v3': 'Raridade e arquétipo gastam o mesmo orçamento dos nossos tacos: ninguém domina ninguém.',
  'admin.colecoes.modeloSobre.piso-do-gratuito': 'O taco grátis é o piso e a raridade soma um extra; o lendário é 100 em tudo.',
  'admin.colecoes.modeloSobre.por-traits': 'O padrão: os pontos saem dos TRAITS da própria NFT — o grátis é o piso e cada categoria alimenta um atributo, quanto mais raro o trait, mais ponto. O arquétipo aqui não muda número nenhum: ele só escolhe a arte de família de quem não tem arte subida. A peça única nasce 100 em tudo. Método do Bunker (PR #27).',
  'admin.colecoes.montador.categoriasPorTraits': 'Categoria de trait → atributo alimentado',
  'admin.colecoes.montador.pecaUnica': 'peça única (100 em tudo)',
  'admin.colecoes.montador.atinge': 'atinge {quantas} NFTs',
  'admin.colecoes.montador.noPiso': '{quantos} das {total} NFTs ficam no piso do taco grátis: nenhuma delas tem trait alimentado. Confira antes de enviar.',
  'admin.colecoes.montador.alimenta': 'Atributo que esta categoria alimenta',
  'admin.colecoes.montador.alvo.nenhum': '— não alimenta —',
  'admin.colecoes.montador.alvo.peso': 'peso',
  'admin.colecoes.montador.alvo.efeito': 'efeito',
  'admin.colecoes.montador.alvo.aderencia': 'aderência',
  'admin.colecoes.montador.alvo.quebra': 'quebra',
  'admin.colecoes.montador.alvo.todos': 'todos (base)',
  'admin.colecoes.montador.erro.sem_alvo': 'Escolha o atributo que ao menos uma categoria alimenta: no por-traits é só daí que saem os pontos. Sem nenhum, toda NFT ficaria no piso do taco grátis.',
  'admin.colecoes.montador.categorias': 'Categoria de trait → arquétipo',
  'admin.colecoes.montador.ignorar': '— não conta —',
  'admin.colecoes.montador.semTraits': 'Lendo os traits da coleção…',
  'admin.colecoes.montador.cortado': 'A coleção tem traits demais: a lista mostra as 32 primeiras categorias e até 500 valores de cada.',
  'admin.colecoes.montador.vazios': 'Valores vazios (não contam para o arquétipo), separados por vírgula',
  'admin.colecoes.montador.vaziosPorTraits': 'Valores vazios (não valem ponto nenhum), separados por vírgula',
  'admin.colecoes.montador.faixas': 'Faixas de raridade (% da coleção, do topo do ranking)',
  'admin.colecoes.montador.faixaAte': '{raridade} até',
  'admin.colecoes.montador.excecoes': 'Exceções (trait = valor → raridade e arquétipo fixos)',
  'admin.colecoes.montador.excecoesPorTraits': 'Exceções (trait = valor → raridade fixa, e a peça única)',
  'admin.colecoes.montador.trait': 'trait',
  'admin.colecoes.montador.valor': 'valor',
  'admin.colecoes.montador.tirar': 'Tirar',
  'admin.colecoes.montador.maisExcecao': 'Mais uma exceção',
  'admin.colecoes.montador.distribuicao': 'Distribuição com estas regras:',
  'admin.colecoes.montador.gerar': 'Gerar pelas regras',
  'admin.colecoes.montador.erro.sem_categoria': 'Escolha o arquétipo de ao menos uma categoria.',
  'admin.colecoes.montador.erro.faixas': 'As faixas vão de 0 a 100 e crescem: épico ≤ raro ≤ incomum.',
  'admin.colecoes.montador.erro.excecao': 'Toda exceção precisa de trait e valor.',
  'admin.colecoes.csv.subir': 'Subir CSV',
  'admin.colecoes.csv.formato': 'Colunas: mint,raridade,arquetipo. Nada é salvo se uma linha tiver erro.',
  'admin.colecoes.csv.recusado': 'CSV recusado:',
  'admin.colecoes.csv.linha': 'Linha {linha}: {motivo}',
  'admin.colecoes.csv.mint_fora_da_colecao': 'mint fora da coleção',
  'admin.colecoes.csv.raridade_invalida': 'raridade inválida',
  'admin.colecoes.csv.arquetipo_invalido': 'arquétipo inválido',
  'admin.colecoes.csv.mint_repetido': 'mint repetido',
  'admin.colecoes.csv.formato_invalido': 'formato inválido',
  'admin.colecoes.arte.arraste': 'Solte aqui vários PNG com nome <número>.png (ou escolha):',
  'admin.colecoes.arte.ok': 'arte do #{numero} salva (v{versao})',
  'admin.colecoes.arte.nomeRuim': 'o nome precisa ser <número>.png',
  'admin.colecoes.arte.naoTentado': 'não enviado: o servidor pediu para ir devagar; tente de novo em 1 minuto',
  'admin.colecoes.lista.titulo': 'NFTs da coleção',
  'admin.colecoes.lista.carregando': 'Carregando…',
  'admin.colecoes.lista.semVersao': 'Ainda não há tabela. Gere a primeira pelas regras ou suba um CSV.',
  'admin.colecoes.lista.soLeituraVersao': 'Esta não é a última versão: a lista é só leitura.',
  'admin.colecoes.lista.nada': 'Nenhuma NFT com estes filtros.',
  'admin.colecoes.lista.contagem': '{visiveis} de {total} NFTs',
  'admin.colecoes.lista.nft': 'NFT',
  'admin.colecoes.lista.mint': 'mint',
  'admin.colecoes.lista.raridade': 'raridade',
  'admin.colecoes.lista.arquetipo': 'arquétipo',
  'admin.colecoes.lista.pontos': 'peso · efeito · aderência · quebra',
  'admin.colecoes.lista.arte': 'arte',
  'admin.colecoes.lista.traits': 'traits',
  'admin.colecoes.lista.copiar': 'copiar',
  'admin.colecoes.lista.familiaPadrao': 'família padrão',
  'admin.colecoes.lista.familiaSo': 'Sem arte subida: o desenho vem desta família. Não muda atributo nenhum.',
  'admin.colecoes.lista.trocarArte': 'trocar arte',
  'admin.colecoes.lista.mudou': 'difere da aprovada',
  'admin.colecoes.lista.previa': 'prévia',
  'admin.colecoes.lista.editada': 'editada',
  'admin.colecoes.lista.mudouCurto': 'mudou',
  'admin.colecoes.lista.antes': 'antes: {valor}',
  'admin.colecoes.lista.mostrarMais': { one: 'Mostrar mais ({count} restante)', other: 'Mostrar mais ({count} restantes)' },
  'admin.colecoes.filtro.raridade': 'toda raridade',
  'admin.colecoes.filtro.arquetipo': 'todo arquétipo',
  'admin.colecoes.filtro.familia': 'Família da arte',
  'admin.colecoes.filtro.trait': 'todo trait',
  'admin.colecoes.filtro.semArte': 'sem arte',
  'admin.colecoes.filtro.busca': 'número ou mint',
  'admin.colecoes.filtro.limpar': 'Limpar filtros',
  'admin.colecoes.trilha.titulo': 'Rastro',
  'admin.colecoes.trilha.vazia': 'Nada registrado ainda.',
  'admin.colecoes.trilha.assinada': 'assinada',
  'admin.colecoes.trilha.sessao': 'sessão',
  'admin.colecoes.trilha.mensagem': 'mensagem',
  'admin.colecoes.copiado': 'Mint copiado.',
  'admin.colecoes.ocupado.lendo': 'Lendo…',
  'admin.colecoes.ocupado.assinando': 'Esperando a assinatura na carteira…',
  'admin.colecoes.ocupado.gerando': 'Salvando uma versão nova…',
  'admin.colecoes.ocupado.enviando': 'Enviando para análise…',
  'admin.colecoes.ocupado.subindo': 'Subindo as artes…',
  'admin.colecoes.ok.rascunho': 'Versão v{versao} salva como rascunho.',
  'admin.colecoes.ok.cadastrada': 'Coleção cadastrada como rascunho.',
  'admin.colecoes.ok.enviada': 'v{versao} enviada para análise.',
  'admin.colecoes.ok.aprovada': 'v{versao} aprovada: os tacos valem no jogo a partir do próximo login.',
  'admin.colecoes.ok.recusada': 'v{versao} recusada.',
  'admin.colecoes.ok.suspensa': 'Coleção suspensa: os tacos saem do jogo no próximo login.',
  'admin.colecoes.ok.reativada': 'Coleção reativada.',
  'admin.colecoes.erro.sem_carteira': 'Conecte a carteira de admin na barra do topo.',
  'admin.colecoes.carteiraMudou': 'A carteira da barra mudou: entre como admin de novo.',
  'admin.colecoes.entrarCom': 'Vai assinar com a carteira {carteira}, a conectada na barra do topo.',
  'admin.colecoes.erro.nao_encontrado': 'O painel não respondeu: admin desligado, IP fora da lista ou carteira que não é admin.',
  'admin.colecoes.erro.devagar': 'Pedidos demais em pouco tempo. Espere um minuto.',
  'admin.colecoes.erro.sessao': 'A sessão acabou (15 minutos). Entre de novo.',
  'admin.colecoes.erro.auditoria_indisponivel': 'O rastro está fora do ar; por segurança, nada foi feito.',
  'admin.colecoes.erro.das_indisponivel': 'A leitura das NFTs (DAS) não respondeu. Tente de novo.',
  'admin.colecoes.erro.assinatura': 'A assinatura não foi aceita (vencida, de outra carteira ou de outra versão). Tente de novo.',
  'admin.colecoes.erro.assinatura_ausente': 'Faltou a assinatura.',
  'admin.colecoes.erro.acao_invalida': 'Ação inválida.',
  'admin.colecoes.erro.transicao_invalida': 'A coleção não está num estado que permite isto. Recarregue.',
  'admin.colecoes.erro.rascunho_invalido': 'A tabela foi recusada (número repetido ou linhas demais).',
  'admin.colecoes.erro.regras_invalidas': 'As regras foram recusadas pelo servidor.',
  'admin.colecoes.erro.linhas_invalidas': 'As linhas editadas foram recusadas (a coleção mudou?). Recarregue.',
  'admin.colecoes.erro.modelo_invalido': 'Modelo de pontos desconhecido.',
  'admin.colecoes.erro.csv': 'CSV recusado.',
  'admin.colecoes.erro.motivo': 'Motivo inválido.',
  'admin.colecoes.erro.versao': 'A versão em análise mudou. Recarregue antes de decidir.',
  'admin.colecoes.erro.versao_inexistente': 'Versão inexistente.',
  'admin.colecoes.erro.suspensa': 'Coleção suspensa: reative antes de aprovar.',
  'admin.colecoes.erro.nada_aprovado': 'Não há versão aprovada para suspender.',
  'admin.colecoes.erro.nao_suspensa': 'A coleção não está suspensa.',
  'admin.colecoes.erro.colecao_inexistente': 'Coleção não cadastrada.',
  'admin.colecoes.erro.colecao_nao_encontrada': 'Coleção não encontrada nesta rede.',
  'admin.colecoes.erro.grupo_vazio': 'Nenhuma NFT verificada nesta coleção.',
  'admin.colecoes.erro.endereco_invalido': 'Endereço inválido (base58, 32 a 44 caracteres).',
  'admin.colecoes.erro.grande_demais': 'Arquivo grande demais.',
  'admin.colecoes.erro.nao_e_png': 'Não é PNG.',
  'admin.colecoes.erro.dimensoes': 'PNG acima de 2048×2048.',
  'admin.colecoes.erro.sem_transparencia': 'PNG sem fundo transparente.',
  'admin.colecoes.erro.nao_parece_vara': 'O desenho não parece uma vara de taco.',
  'admin.colecoes.erro.numero': 'Número inválido.',
  'admin.colecoes.erro.numero_inexistente': 'Número fora da tabela.',
  'admin.colecoes.erro.sem_rascunho': 'Gere a tabela antes de subir artes.',
  'admin.colecoes.erro.json': 'Pedido malformado.',
  'admin.colecoes.erro.interno': 'Erro interno do servidor.',
  'admin.colecoes.erro.outro': 'O servidor recusou ({codigo}).',


  // ----------------------------------------------------------------- efeito
  'efeito.centro': 'centro',

  // --------------------------------------------- status da partida (código)
  'status.quebra': 'Quebra',
  'status.disputa': 'Disputa da saída · rodada {rodada} de {total}',
  'status.mesa_aberta': 'Mesa aberta',
  'livre.status': 'Modo livre',
  'livre.quantas': 'Bolas',
  'livre.cheia': 'Mesa cheia',
  'livre.repetir': 'Repetir a jogada',
  'livre.sortear': 'Sortear de novo',
  'livre.voltarNormal': 'Partida normal',
  'livre.refazer': 'Refazer no modo livre',
  'livre.mover': 'Mover bolas',
  'livre.moverPronto': 'Pronto',
  'livre.dicaMover': 'Arraste qualquer bola para mudá-la de lugar. "Pronto" volta a mirar.',
  'treino.escolha.titulo': 'Como você quer treinar?',
  'treino.escolha.normal': 'Partida normal',
  'treino.escolha.normalExplica': 'Com regras e triângulo, como uma partida de verdade.',
  'treino.escolha.livre': 'Modo livre',
  'treino.escolha.livreExplica': 'Sem regras: escolha quantas bolas e repita a jogada quantas vezes quiser.',
  'treino.escolha.mini': 'Mini jogo',
  'treino.escolha.miniExplica': 'A disputa de quem quebra: deixe a bola 1 o mais perto do alvo e bata a sua marca.',
  'miniJogo.recorde': 'Nova melhor marca: {cm} cm!',
  'miniJogo.ultima': 'Última',
  'miniJogo.melhor': 'Melhor',
  'miniJogo.caiu': 'caiu',
  'miniJogo.tentativas': { one: '{count} tacada', other: '{count} tacadas' },
  'miniJogo.sortear': 'Sortear',
  'miniJogo.sortearDica': 'Sortear outra posição, como no mini jogo da partida',
  'miniJogo.mover': 'Mover',
  'miniJogo.moverDica': 'Arrastar a branca, a bola 1 e o alvo',
  'miniJogo.congelar': 'Congelar',
  'miniJogo.congelarDica': 'Deixa o alvo parado onde está, para treinar com alvo fixo. Toque de novo para ele voltar a pular',
  'miniJogo.brancaCaiu': 'Falta: a branca caiu',
  'miniJogo.falta': 'falta',
  'treino.tipo': 'Tipo de treino',
  'status.na_bola_8': 'Na bola 8',
  'status.faltam': { one: 'Falta {n}', other: 'Faltam {n}' },
  'status.venceu': 'Jogador {jogador} venceu',
  'status.mesa_vazia': 'Mesa vazia',
  'status.bola_da_vez': 'Bola da vez: {bola}',
  'bola.1': 'vermelha',
  'bola.2': 'amarela',
  'bola.3': 'verde',
  'bola.4': 'marrom',
  'bola.5': 'azul',
  'bola.6': 'rosa',
  'bola.7': 'preta',

  // ------------------------------------------------------------ julgamento
  'falta.generica': 'Falta.',
  'falta.cue-ball-pocketed': 'Branca na caçapa.',
  'falta.cue-ball-off-table': 'Branca fora da mesa.',
  'falta.ball-off-table': 'Bola fora da mesa.',
  'falta.no-contact': 'Não tocou em nenhuma bola.',
  'falta.wrong-ball-first': 'Bateu na bola errada.',
  'falta.eight-ball-first-illegal': 'Não pode bater na 8 ainda.',
  'falta.no-rail-after-contact': 'Nenhuma bola foi à tabela.',
  'falta.no-call': 'Faltou declarar a caçapa.',
  'falta.wrong-ball-called': 'Encaçapou outra bola.',
  'falta.free-ball-missed': 'Errou a bola livre.',
  'falta.wrong-ball-potted': 'Encaçapou bola que não era a da vez.',
  'julgamento.pontos': '+{n} pontos',
  'julgamento.segue': 'Encaçapou — segue na mesa.',

  // ------------------------------------------------------ disputa da saída
  // O mini jogo antes da quebra: quem deixar a bola mais perto do alvo estoura.
  'disputa.titulo': 'Disputa da saída',
  'disputa.explicacao': 'Deixe a bola colorida o mais perto possível do centro do alvo. Quem chegar mais perto estoura.',
  'disputa.rodada': 'Rodada {n} de {total}',
  'disputa.resultado': 'A {cm} cm do alvo',
  'disputa.caiu': 'A bola caiu na caçapa',
  'disputa.semTacada': 'Sem tacada a tempo',
  'disputa.empate': 'Empate! Nova rodada, com a ordem invertida.',
  'disputa.voceEstoura': 'Você venceu a disputa e estoura!',
  'disputa.adversarioEstoura': 'O adversário venceu a disputa e estoura.',
  'disputa.jogadorEstoura': 'O jogador {n} venceu a disputa e estoura.',
  'disputa.marca.voce': 'você',
  'disputa.marca.adversario': 'adversário',
  'disputa.marca.jogador': 'J{n}',

  // ------------------------------------------------------- mesa (controle)
  'partida.limiteTacadas': 'Limite de tacadas do replay atingido.',
  'partida.limiteDeclaracoes': 'Limite de declarações do replay atingido.',
  'partida.limitePosicionamentos': 'Limite de posicionamentos do replay atingido.',
  'partida.desync': 'mesa divergiu do servidor ({nosso} ≠ {servidor})',

  // -------------------------------------------------------------------- hud
  'hud.vezDoJogador': 'Vez do jogador {n}',
  'hud.dica.escolha': 'Escolha uma das opções na mesa',
  'hud.dica.bolaNaMao': 'Solte a branca onde quiser jogar',
  'hud.dica.declare': 'Declare a caçapa antes de tacar',
  'hud.dica.arraste': 'Arraste a partir da branca e solte para tacar',

  // ----------------------------------------------------------------- placar
  // Os dois cartões acima da mesa. Nasceu do pedido "placar bem claro — a
  // galera tira foto e posta depois": texto curto, grande, igual nos dois modos.
  'placar.voce': 'Você',
  'placar.adversario': 'Adversário',
  'placar.jogador1': 'Jogador 1',
  'placar.jogador2': 'Jogador 2',
  'placar.faltam': { one: 'falta', other: 'faltam' },
  'placar.pontos': { one: 'ponto', other: 'pontos' },
  'placar.soA8': 'só a 8',
  'placar.mesaAberta': 'mesa aberta',
  'placar.naDisputa': 'disputa da saída',
  'placar.naVez': 'na vez',
  'placar.suaVez': 'sua vez',
  'placar.venceu': 'venceu',

  // ---------------------------------------------------------- cartão do fim
  // A tela de resultado "para foto" e a imagem 1080×1080 que ela exporta.
  'cartao.venceuJogador': 'Jogador {n} venceu',
  'cartao.vencedor': 'vencedor',
  'cartao.treino': 'Mesa de treino',
  'cartao.mesaApostada': 'Mesa apostada',
  'cartao.duracao': 'Duração',
  'cartao.tacadas': 'Tacadas',
  'cartao.aposta': 'Aposta',
  'cartao.apostaValor': '{valor} {simbolo} cada · pote {pote} {simbolo}',
  'cartao.encacapadas': 'encaçapadas',
  'cartao.compartilhar': 'Compartilhar resultado',
  'cartao.copiar': 'Copiar imagem',
  'cartao.baixar': 'Baixar imagem',
  'cartao.copiada': 'Imagem copiada!',
  'cartao.baixada': 'Imagem baixada.',
  'cartao.falhou': 'Não deu para gerar a imagem.',
  'cartao.rodape': 'Partida jogada em SOL Pool · física determinística, replay na blockchain',
  'duracao.segundos': '{s} s',
  'duracao.minutos': '{min} min {s} s',
  'duracao.horas': '{h} h {min} min',
  'duracao.dias': '{d} d {h} h',
  'decisao.jogadorEscolhe': 'Jogador {n} escolhe',
  'decisao.voceEscolhe': 'Você escolhe',
  'decisao.recomendado': '{opcao} (recomendado)',
  'cacapa.toque': 'Toque na caçapa onde a bola 8 vai cair',
  'cacapa.toqueBola': 'Toque na caçapa onde sua bola vai cair',
  'cacapa.declarada': 'declarada',

  // ------------------------------------------------------------------- mesa
  'mesa.ajusteFino': '◎ Ajuste fino',
  'mesa.ajusteFinoAjuda':
    'Move a mira cinco vezes mais devagar, para escolher o ponto exato (ou segure Shift)',
  'mesa.travarMira': '🔒 Travar mira',
  'mesa.travarMiraAjuda':
    'Z — congela o ângulo: o arraste passa a mexer só na força. Continua travada entre tacadas.',
  'mesa.som': 'Som',
  'mesa.mudo': 'Mudo',
  'mesa.ligarSom': 'Ligar o som',
  'mesa.silenciar': 'Silenciar',
  'mesa.novaPartida': 'Nova partida',
  'mesa.pote': 'Pote {valor} {simbolo}',
  'mesa.modalidade': 'Modalidade',
  'mira.rotulo': 'Mira',
  'treino.taco': 'Taco de teste',
  'treino.tacoPadrao': 'Padrão do jogo',
  'mesa.voltar': 'Voltar',
  'mesa.lobby': '← Lobby',
  'mesa.desistir': 'Desistir',
  'mesa.conectando': 'conectando…',

  // ----------------------------------------------------------- mesa em rede
  'net.semCarteiraTitulo': 'Nenhuma carteira encontrada',
  'net.semCarteiraTexto':
    'Instale uma carteira Solana — Phantom, Solflare, Backpack e outras servem — e recarregue para jogar uma mesa apostada.',
  'net.voltarAoLobby': 'Voltar ao lobby',
  'net.travou.titulo': 'Algo deu errado ao começar a partida',
  'net.travou.motivo': 'Motivo: {motivo}',
  'net.travou.semMotivo':
    'A quebra não foi combinada. O adversário pode ter fechado a página, ou aberto a mesa em outro navegador.',
  'net.travou.volta':
    'Suas entradas voltam sozinhas em {contador}. Não precisa fazer nada: pode voltar ao lobby ou fechar esta página.',
  'net.travou.vencido':
    'O prazo da mesa venceu: o servidor está devolvendo as entradas. Pode levar alguns minutos.',
  'net.travou.desconhecido': 'Suas entradas voltam sozinhas quando o prazo da mesa vencer.',
  'net.demo': 'DEMONSTRAÇÃO — mesa local, sem carteira e sem aposta. É só a tela desta página.',
  'net.confirmarDesistir': 'Desistir entrega o pote ao adversário. Confirma?',
  'net.etapa.aguardando': 'Encontrar adversário',
  'net.etapa.comprometendo': 'Combinar a quebra',
  'net.etapa.revelando': 'Revelar a quebra',
  'net.explicacao.aguardando':
    'Sua mesa está aberta e o depósito já está travado no contrato. A partida começa assim que alguém entrar.',
  'net.explicacao.comprometendo':
    'Você e o adversário estão sorteando a quebra juntos: cada um envia um número lacrado, e nenhum dos dois — nem o servidor — consegue escolher o resultado sozinho.',
  'net.explicacao.revelando':
    'Os dois números foram abertos. A mesa está sendo montada com a semente que eles formam.',
  'net.preparando': 'Preparando a mesa',
  'net.sessao.titulo': 'Entre para voltar à mesa',
  'net.sessao.texto':
    'Sua sessão não vale mais — o servidor foi reiniciado ou ela expirou. Assine o login e a partida continua de onde parou.',
  'net.sessao.assinar': 'Assinar login',
  'net.sessao.aguardandoCarteira': 'Aguardando a carteira…',
  'net.adversarioFora': 'Adversário fora — a vez dele passa sozinha',
  'net.fase.aguardando': 'Aguardando uma mesa fechar no lobby…',
  'net.fase.comprometendo': 'Combinando a quebra…',
  'net.fase.revelando': 'Revelando a quebra…',
  'net.suaVez': 'Sua vez',
  'net.vezDoAdversario': 'Vez do adversário',
  'net.voceVenceu': 'Você venceu',
  'net.vocePerdeu': 'Você perdeu',
  'net.partidaAnulada': 'Partida anulada',
  'net.por': 'por {motivo}',
  'net.desyncTitulo': 'A mesa saiu de sincronia com o servidor.',
  'net.desyncTexto':
    'O resultado da partida continua correto e será liquidado normalmente. Recarregue para reacompanhar.',
  'net.cliqueCacapa': 'Clique na caçapa onde a bola 8 vai entrar.',
  'net.adversarioNaBola8': 'Adversário está na bola 8…',
  'net.escolhaCacapa': 'Sua vez: escolha a caçapa da tacada para liberar o taco.',
  'net.adversarioEscolhendoCacapa': 'Adversário escolhendo a caçapa…',
  'net.escolhaNaMesa': 'Escolha na mesa.',
  'net.adversarioEscolhendo': 'Adversário está escolhendo…',
  'net.entradasVoltam': 'As entradas voltam para os dois pelo contrato.',
  'net.enviandoLiquidacao': 'Enviando o pagamento e o replay para a blockchain…',
  'net.pagamentoGravado': 'Pagamento e replay gravados.',
  'net.verTransacao': 'ver a transação ↗',
  'net.naoLiquidada':
    'Não foi possível liquidar agora ({motivo}). As entradas voltam pelo prazo do contrato.',
  'net.motivoDesconhecido': 'motivo desconhecido',
  'net.combinandoQuebra': 'Combinando a quebra com o adversário…',
  'net.partidaRetomada': 'Partida retomada.',
  'net.adversarioDesconectou': 'Adversário desconectou — a vez dele vai passando.',
  'net.adversarioVoltou': 'Adversário voltou.',

  // ------------------------------------------------ cartão do jogador (mesa)
  'cartaoJogador.fechar': 'Fechar',
  'cartaoJogador.carregando': 'Carregando estatísticas…',
  'cartaoJogador.indisponivel': 'Estatísticas indisponíveis neste servidor.',
  'cartaoJogador.erro': 'Não foi possível carregar as estatísticas.',
  'cartaoJogador.novato': 'Primeira partida registrada: ainda sem histórico.',
  'cartaoJogador.aproveitamento': 'vitórias',
  'cartaoJogador.vd': 'V–D',
  'cartaoJogador.partidas': 'partidas',
  'cartaoJogador.ultimas': 'Últimas',
  'cartaoJogador.v': 'V',
  'cartaoJogador.d': 'D',
  'cartaoJogador.semana': 'Semana',
  'cartaoJogador.posicao': '{n}º',
  'cartaoJogador.pontos': '{pontos} pts',

  // ------------------------------------------------ formatos e eventos de mesa
  'formato.titulo': 'Formato',
  'formato.unica': 'Partida única',
  'formato.unicaAjuda': 'Com aposta, como sempre.',
  'formato.melhorDe3': 'Melhor de 3',
  'formato.melhorDe3Ajuda': 'Amistoso · ganha quem vencer 2 partidas.',
  'formato.torneio': 'Torneio (4 jogadores)',
  'formato.unicaCurto': '1x1',
  'formato.torneioCurto': 'Torneio',
  'formato.emTestes': 'em testes',
  // O aviso, na lista do 1x1, dos eventos abertos (`htmlDoAvisoDeEventos`).
  'formato.avisoInicio': 'Há ',
  'formato.avisoTorneios': { one: '{count} torneio aberto', other: '{count} torneios abertos' },
  'formato.avisoSeries': { one: '{count} melhor de 3 aberta', other: '{count} melhores de 3 abertas' },
  'formato.avisoE': ' e ',
  'formato.verTorneios': 'Ver torneios',
  'formato.verSeries': 'Ver melhores de 3',
  'formato.emTestesAviso': 'Este formato ainda está em testes: pode falhar ou mudar sem aviso. O 1x1 é o modo estável.',
  'formato.umAberto': '1 aberto',
  'formato.abertos': '{n} abertos',
  'formato.torneioAjuda': 'Amistoso · semifinais e final.',
  'eventoMesa.amistoso': 'amistoso',
  'eventoMesa.amistosoAviso': 'Amistoso: sem aposta nesta fase. Entrou, joga até o fim; quem perder no torneio pode assistir.',
  'eventoMesa.nome': 'Nome do evento',
  'eventoMesa.entradaRotulo': 'Entrada do torneio',
  'eventoMesa.entradaRotuloSerie': 'Entrada da melhor de 3',
  'eventoMesa.entradaFaixaSerie':
    'Cobrada uma vez de cada um dos dois, ao entrar. De {min} a {max} {simbolo}.',
  'eventoMesa.itemVencedorLeva': 'Quem ganhar a série leva {valor}',
  'eventoMesa.custoDeAbrirASerie':
    'Abrir a sala cobra a SUA entrada mais {valor} de aluguel da conta na blockchain, numa assinatura só — você joga a série, não só organiza. O aluguel fica na conta e hoje não é devolvido.',
  'eventoMesa.entradaFaixa': 'Cobrada uma vez, ao se inscrever. De {min} a {max} {simbolo}.',
  'eventoMesa.entradaIndisponivel': 'Torneio com entrada indisponível agora: só amistoso.',
  'eventoMesa.campeaoLeva': 'O campeão leva {valor}',
  'eventoMesa.pagamentoFeito': 'Pago: {valor} SOL para {quem}',
  'eventoMesa.pagamentoFeitoSemValor': 'Prêmio pago na chain para {quem}',
  'eventoMesa.pagamentoVoce': 'você',
  'eventoMesa.pagamentoACaminho': 'Pagamento do prêmio em processamento na chain…',
  'eventoMesa.pagamentoCancelado': 'Torneio cancelado: cada inscrito pode sacar a entrada',
  'eventoMesa.entradaDoPainel': 'Entrada {valor} SOL',
  'eventoMesa.custoDeAbrirASala':
    'Abrir a sala custa {valor} de aluguel da conta do torneio, cobrado de você ao assinar. Não é inscrição: quem cria não joga. Esse valor fica na conta e hoje não é devolvido.',
  'eventoMesa.assinandoCriacao': 'Confirme na carteira para abrir o torneio…',
  'eventoMesa.assinandoInscricao': 'Confirme na carteira para se inscrever…',
  'eventoMesa.cancelarTorneio': 'Cancelar e devolver',
  'eventoMesa.sacarInscricao': 'Sacar minha inscrição',
  'eventoMesa.cancelando': 'Cancelando o torneio e devolvendo as inscrições…',
  'eventoMesa.cancelado': 'Torneio cancelado. Cada inscrito pode sacar o depósito.',
  'eventoMesa.sacado': 'Inscrição devolvida à sua carteira.',
  'eventoMesa.criar': 'Criar evento',
  'eventoMesa.naLista': 'Amistoso',
  'eventoMesa.entradaNaLista': '{valor} {simbolo} · pote {pote}',
  'eventoMesa.vagas': '{n} de {total} jogadores',
  'eventoMesa.fechaEm': 'se desfaz em {tempo}',
  'eventoMesa.entrar': 'Entrar',
  'eventoMesa.assistir': 'Assistir',
  'eventoMesa.assistirOutras': 'Assistir as partidas que faltam',
  'eventoMesa.sair': 'Sair do evento',
  'eventoMesa.irParaMesa': 'Ir para a mesa',
  'eventoMesa.fechar': 'Fechar',
  'eventoMesa.jogando': 'em jogo',
  'eventoMesa.jogadores': 'Jogadores',
  'eventoMesa.convite': 'Convite (mande o link)',
  'eventoMesa.conviteComSenha': 'Este evento tem senha: mande a senha junto com o link.',
  'eventoMesa.senhaExplica': 'Só entra quem souber a senha. Ela não é salva no sistema.',
  'eventoMesa.vozExplica': 'Conversem por voz durante as partidas.',
  // O "Criar mesa" do evento, no mesmo desenho da 1x1 (`htmlDoFormularioDoEvento`).
  'eventoMesa.etapaUnica': 'Etapa única · sem depósito',
  'eventoMesa.placeholderSerie': 'Ex.: Revanche de sexta',
  'eventoMesa.placeholderTorneio': 'Ex.: Torneio da galera',
  'eventoMesa.comoFunciona': 'Como funciona',
  'eventoMesa.jogadoresSerie': '2',
  'eventoMesa.jogadoresTorneio': '4',
  'eventoMesa.partidas': 'Partidas',
  'eventoMesa.partidasSerie': 'até 3 · vence quem ganhar 2',
  'eventoMesa.partidasTorneio': '3 · duas semifinais e a final',
  'eventoMesa.aposta': 'Aposta',
  'eventoMesa.semAposta': 'nenhuma · amistoso',
  'eventoMesa.plateia': 'Plateia',
  'eventoMesa.plateiaAberta': 'Qualquer um pode assistir',
  'criar.configuracao': 'Configuração da partida',
  'criar.opcoes': 'Opções da partida',
  'criar.resumo': 'Resumo da aposta',
  'criar.vocePaga': 'Você paga',
  'criar.poteTotal': 'Pote total',
  'criar.prazosDaPartida': 'Prazos da partida',
  'criar.opcional': '(opcional)',
  'criar.senhaFaixa': '{min} a {max} caracteres.',
  'criar.subtitulo.unica': 'Partida única com aposta. Entrou, joga até o fim.',
  'criar.subtitulo.melhor-de-3': 'Melhor de 3 partidas. Amistoso, sem aposta.',
  'criar.subtitulo.torneio': 'Torneio de 4 jogadores. Amistoso, semifinais e final.',
  'formato.emTestesCurto': 'Este formato ainda está em testes: pode falhar ou mudar sem aviso.',
  'eventoMesa.amistosoCurto': 'Amistoso: sem aposta nesta fase.',
  'eventoMesa.configuracaoTorneio': 'Configuração do torneio',
  'eventoMesa.nomeTorneio': 'Nome do torneio',
  'eventoMesa.formatoTorneio': 'Formato do torneio',
  'eventoMesa.sequencia': 'Sequência de partidas',
  'eventoMesa.partida1': '1ª partida',
  'eventoMesa.partida2': '2ª partida',
  'eventoMesa.partida3': '3ª partida (se necessário)',
  'eventoMesa.semi1': 'Semi 1',
  'eventoMesa.semi2': 'Semi 2',
  'eventoMesa.jogadorN': 'Jogador {n}',
  'eventoMesa.vencedoresDasSemis': 'Vencedores das semifinais',
  'eventoMesa.chaveDescricao': 'Semifinal 1: jogadores 1 e 2. Semifinal 2: jogadores 3 e 4. Os vencedores fazem a final.',
  'eventoMesa.itemJogadoresSerie': '2 jogadores',
  'eventoMesa.itemPartidasSerie': 'Até 3 partidas',
  'eventoMesa.itemVenceSerie': 'Vence quem ganhar 2',
  'eventoMesa.itemJogadoresTorneio': '4 jogadores',
  'eventoMesa.itemPartidasTorneio': '2 semifinais e 1 final',
  'eventoMesa.itemSemAposta': 'Sem aposta · amistoso',
  'eventoMesa.itemEliminadoAssiste': 'Quem perder pode assistir',
  // O painel do meu evento (`htmlDoPainelDoEvento`).
  'eventoMesa.estado.aberto': 'Aguardando',
  'eventoMesa.estado.jogando': 'Em andamento',
  'eventoMesa.estado.terminado': 'Encerrado',
  'eventoMesa.estado.desfeito': 'Desfeito',
  'eventoMesa.vagaLivre': 'Aguardando jogador…',
  'eventoMesa.criador': 'Criador',
  'eventoMesa.conviteTitulo': 'Convite',
  'eventoMesa.conviteExplica': 'Mande o link para quem vai jogar: ele abre direto neste evento.',
  'eventoMesa.plateiaFechada': 'Plateia fechada: só quem joga',
  'eventoMesa.plateiaFechadaTorneio': 'Plateia fechada: só os 4 do torneio',
  'eventoMesa.plateiaFechadaCartao': 'Plateia fechada',
  'eventoMesa.plateiaExplicaSerie': 'Qualquer pessoa pode assistir à série.',
  'eventoMesa.plateiaExplicaTorneio': 'Qualquer pessoa pode assistir ao torneio.',
  'eventoMesa.comoFuncionaSerie': 'Começa quando o 2º jogador entra. Sem ele em 10 minutos, o evento é desfeito.',
  'eventoMesa.comoFuncionaTorneio': 'Começa quando os 4 entram: as semifinais são juntas. Sem completar em 10 minutos, é desfeito.',
  'eventoMesa.prazoDuracaoPartida': 'Por partida',
  'eventoMesa.voce': 'Você',
  'eventoMesa.esperando': 'Esperando jogadores: faltam {n}.',
  'eventoMesa.jogandoAgora': 'Sua partida está em jogo.',
  'eventoMesa.proximaQuebra': 'A próxima partida começa em instantes: {nome} quebra.',
  'eventoMesa.proximaEmInstantes': 'A próxima partida começa em instantes.',
  'eventoMesa.naFinal': 'Você está na final! Esperando a outra semifinal acabar.',
  'eventoMesa.eliminado': 'Você foi eliminado. Pode assistir as partidas que faltam.',
  'eventoMesa.voceCampeao': 'Você é o campeão!',
  'eventoMesa.campeao': '{nome} é o campeão!',
  'eventoMesa.dividido': 'A final travou: {a} e {b} dividem o título.',
  'eventoMesa.semCampeao': 'O evento terminou sem campeão ({motivo}).',
  'eventoMesa.desfeito': 'O evento se desfez ({motivo}).',
  'eventoMesa.motivo.todos_sairam': 'todos saíram',
  'eventoMesa.motivo.partida_travou': 'uma partida da série travou',
  'eventoMesa.motivo.final_travou': 'a final travou',
  'eventoMesa.motivo.semifinal_travou': 'uma semifinal travou',
  'eventoMesa.motivo.nao_fechou': 'não fechou a tempo',
  'eventoMesa.motivo.passou_do_tempo': 'o evento passou do tempo máximo',
  'eventoMesa.motivo.cancelado_pelo_dono': 'Cancelado por quem criou',
  'eventoMesa.semifinal': 'Semifinal {n}',
  'eventoMesa.final': 'Final',

  // ------------------------------------------------------------- espectador
  'net.assistindo': 'Assistindo',
  'net.assistindoAguardando': 'Assistindo — aguardando a partida começar…',
  'net.vezDoJogador': 'Vez do jogador {n}',
  'net.jogadorVenceu': 'Jogador {n} venceu',
  'net.jogadorNaBola8': 'Jogador {n} está na bola 8…',
  'net.jogadorEscolhendoCacapa': 'Jogador {n} escolhendo a caçapa…',
  'net.jogadorEscolhendo': 'Jogador {n} está escolhendo…',
  'net.plateia': { one: '{count} assistindo', other: '{count} assistindo' },
  'net.naoDaParaAssistir': 'Não dá para assistir',
  'net.senhaTitulo': 'Esta mesa tem senha',
  'net.senhaExplica': 'Para assistir, digite a senha que quem criou a mesa combinou com você.',
  'net.senhaEnviar': 'Assistir',

  // --------------------------------------------------------------- conversa
  'chat.titulo': 'Conversa',
  'chat.placeholder': 'Diga algo à mesa…',
  'chat.placeholderDemo': 'Sem servidor no modo demonstração',
  'chat.enviar': 'Enviar',
  // As frases rápidas (`FRASES_RAPIDAS` em `ui/chat.ts`): um toque e vai.
  'chat.rapidas': 'Frases rápidas',
  'chat.rapida.boaSorte': 'Boa sorte!',
  'chat.rapida.boaJogada': 'Boa jogada!',
  'chat.rapida.quase': 'Quase!',
  'chat.rapida.valeu': 'Valeu!',
  'chat.rapida.gg': 'GG',
  'chat.rapida.oi': 'Oi!',
  'chat.rapida.queTacada': 'Que tacada!',
  'chat.rapida.revanche': 'Revanche?',
  'chat.rapida.hojeNao': 'Hoje não!',
  'chat.rapida.sorteDePrincipiante': 'Sorte de principiante',
  'chat.rapida.muitoFraco': 'Muito fraco!',
  'chat.rapida.muitoForte': 'Muito forte!',
  'chat.maisFrases': 'Mais frases',
  'chat.rapida.bomJogo': 'Bom jogo!',
  'chat.rapida.ateAProxima': 'Até a próxima!',
  'chat.rapida.tchau': 'Tchau!',
  'chat.rapida.mandouBem': 'Mandou bem!',
  'chat.rapida.jogouMuito': 'Jogou muito!',
  'chat.rapida.queAula': 'Que aula!',
  'chat.rapida.taNervoso': 'Tá nervoso?',
  'chat.rapida.pressao': 'Pressão!',
  'chat.rapida.suaVez': 'Sua vez!',
  'chat.rapida.pensando': 'Pensando…',
  'chat.rapida.jaVolto': 'Já volto',
  'chat.rapida.foiMal': 'Foi mal!',
  'chat.aba.saudar': 'Saudar',
  'chat.aba.elogiar': 'Elogiar',
  'chat.aba.provocar': 'Provocar',
  'chat.aba.avisos': 'Avisos',
  'chat.vazio': 'Ninguém disse nada ainda.',
  'chat.voce': 'você',
  'chat.adversario': 'adversário',
  'chat.jogador': 'jogador {n}',
  'chat.naSala': 'A conversa começa aqui e continua na mesa.',

  // ------------------------------------------------------------- perfil
  'perfil.rotulo': 'Apelido',
  'perfil.placeholder': 'Como quer ser chamado?',
  'perfil.salvar': 'Salvar',
  'perfil.salvando': 'Salvando…',
  'perfil.salvo': 'Apelido salvo.',
  'perfil.explica':
    'Aparece no lugar do endereço — sempre com ele ao lado, porque apelidos não são únicos.',

  // ------------------------------------------------------------ modalidades
  // Nome e descrição de cada modalidade. O pt-BR é conferido contra
  // `GAME_MODE_INFO` do engine-rules por teste: se a descrição mudar lá, muda
  // aqui — e o inglês, que o tipo obriga a existir, é revisto junto.
  'modo.eightball.nome': '8-Ball',
  'modo.eightball.descricao': 'Lisas contra listradas. Quem limpar o grupo e encaçapar a 8 vence.',
  'modo.sinuca.nome': 'Sinuca brasileira',
  'modo.sinuca.descricao': 'Sete coloridas em ordem crescente. Vence quem fizer mais pontos.',

  // ------------------------------------------------------- regras (página)
  'regras.tituloPagina': 'SOL Pool — Regras',
  'regras.cabecalho': 'Regras',
  'regras.intro':
    'Tudo nesta página é lido do código que julga as partidas: os conjuntos de regras, cada falta e cada jeito de perder, os prazos e os limites. Não há texto solto para desatualizar — o que está aqui é o que o servidor cobra.',
  'regras.fisica':
    'Física v{versao}, digest <code>{digest}</code>. A mesma tacada produz a mesma mesa em qualquer navegador; a <a href="/determinism">página de determinismo</a> prova isso no seu.',
  'regras.bolas': { one: '{count} bola numerada', other: '{count} bolas numeradas' },

  'regras.secao.modalidades': 'Modalidades',
  'regras.secao.conjunto': 'Conjunto de regras em uso',
  'regras.secao.comparacao': 'Em uso, WPA e bar, lado a lado',
  'regras.secao.faltas': 'Faltas',
  'regras.secao.fins': 'Como a partida termina',
  'regras.secao.decisoes': 'Decisões depois da quebra',
  'regras.secao.prazos': 'Prazos',
  'regras.secao.limites': 'Limites do replay',

  'regras.col.regra': 'regra',
  'regras.col.valor': 'valor',
  'regras.col.oQueSignifica': 'o que significa',
  'regras.col.emUso': 'em uso',
  'regras.col.wpa': 'WPA',
  'regras.col.bar': 'bar',
  'regras.col.consequencia': 'consequência',
  'regras.sim': 'sim',
  'regras.nao': 'não',
  'regras.iguais': 'O conjunto em uso é idêntico ao da WPA.',
  'regras.diferencas': {
    one: '{count} diferença em relação à WPA, destacada.',
    other: '{count} diferenças em relação à WPA, destacadas.',
  },

  // Cada campo de `RuleSet` (8-Ball). Um campo novo sem explicação não compila.
  'regras.campo.callShot': 'Quando é preciso declarar bola e caçapa antes de tacar.',
  'regras.campo.callShot.always': 'em toda tacada',
  'regras.campo.callShot.eight-only': 'só na bola 8',
  'regras.campo.callShot.never': 'nunca',
  'regras.campo.callShot.pocket': 'a caçapa, em toda tacada',
  'regras.campo.breakMinBallsToRail':
    'Quantas bolas numeradas precisam tocar a tabela para a quebra valer, se nenhuma for encaçapada. Abaixo disso a quebra é irregular e o adversário escolhe o que fazer.',
  'regras.campo.breakMinBallsToRail.valor': {
    one: '{count} bola à tabela',
    other: '{count} bolas à tabela',
  },
  'regras.campo.kitchenAfterBreakScratch':
    'Falta na quebra restringe a bola na mão à cozinha (atrás da linha da cabeça). Fora da quebra, bola na mão é sempre em qualquer lugar.',
  'regras.campo.eightOnBreakLoses':
    'Encaçapar a 8 na quebra perde a partida. Quando não, o quebrador escolhe entre recolocar a 8 e seguir, ou quebrar de novo.',
  'regras.campo.threeFoulRule':
    'Três faltas seguidas do mesmo jogador entregam a partida. Sem aviso prévio: a contagem está na tela dos dois o tempo todo.',

  // Cada campo de `SinucaRuleSet`.
  'regras.sinuca.foulPoints': 'Pontos que o adversário ganha a cada falta.',
  'regras.sinuca.foulPoints.valor': { one: '{count} ponto', other: '{count} pontos' },
  'regras.sinuca.freeBallAfterPot':
    'Depois de encaçapar a bola da vez, o jogador pode declarar uma bola livre. Acertando, pontua; errando, leva a penalidade.',
  'regras.sinuca.endOnUnreachableScore':
    'A partida acaba quando a diferença de pontos passa do que ainda há na mesa — não se arrasta até a última bola.',
  'regras.sinuca.ordem':
    'A bola da vez é sempre a menor ainda na mesa; cada bola vale o próprio número. Bola encaçapada indevidamente volta para a mesa. Se a mesa esvaziar empatada, a preta volta e a partida se decide nela, com quem não quebrou tacando.',

  // O que é cada falta. A frase curta (`falta.<código>`) é a que aparece na mesa.
  'regras.falta.cue-ball-pocketed': 'A branca caiu numa caçapa.',
  'regras.falta.cue-ball-off-table': 'A branca pulou para fora da mesa.',
  'regras.falta.ball-off-table':
    'Uma bola numerada pulou para fora da mesa. No 8-Ball ela não volta; na sinuca, volta ao lugar.',
  'regras.falta.no-contact':
    'A branca não tocou em bola nenhuma. É também o que o relógio registra quando o prazo de tacada estoura.',
  'regras.falta.wrong-ball-first':
    'A primeira bola tocada não era um alvo válido: uma do grupo do adversário no 8-Ball, ou uma que não era a bola da vez na sinuca.',
  'regras.falta.eight-ball-first-illegal':
    'A branca tocou primeiro na 8 antes de o jogador limpar o próprio grupo. Com a mesa aberta também é falta — "aberta" não quer dizer que tudo vale.',
  'regras.falta.no-rail-after-contact':
    'Depois do contato, nenhuma bola foi encaçapada nem tocou a tabela. É o que impede estacionar a branca sem tentar nada.',
  'regras.falta.no-call': 'A tacada exigia declarar bola e caçapa, e nada foi declarado.',
  'regras.falta.free-ball-missed': 'O jogador declarou uma bola livre e não a acertou primeiro.',
  'regras.falta.wrong-ball-potted':
    'Caiu uma bola que não era a da vez nem a livre declarada. Ela volta para a mesa, mas a falta conta.',

  // Consequências, montadas a partir do conjunto de regras em uso.
  'regras.efeito.bolaNaMao': 'Bola na mão para o adversário, em qualquer ponto da mesa.',
  'regras.efeito.bolaNaMaoCozinha': 'Se a falta foi na quebra, a bola na mão fica restrita à cozinha.',
  'regras.efeito.contaTres': 'Conta para a regra das três faltas seguidas.',
  'regras.efeito.naBola8': 'Encaçapar a 8 na mesma tacada é derrota.',
  'regras.efeito.pontos': {
    one: '{count} ponto para o adversário.',
    other: '{count} pontos para o adversário.',
  },
  'regras.efeito.voltam': 'Bolas encaçapadas na tacada voltam para a mesa; a vez passa.',
  'regras.efeito.brancaNaMao': 'O adversário recebe a branca na mão.',
  'regras.efeito.brancaFica': 'A branca fica onde parou.',

  // Como a partida termina, por código de `MatchEnding` / `SinucaEnding`.
  'regras.fim.eight-ball-potted':
    'Vitória: o grupo estava limpo e a 8 caiu na caçapa declarada, sem falta.',
  'regras.fim.eight-ball-early': 'Derrota: a 8 caiu antes de o jogador limpar o próprio grupo.',
  'regras.fim.eight-ball-wrong-pocket':
    'Derrota: a 8 caiu, mas não na caçapa declarada — ou a bola declarada não era a 8.',
  'regras.fim.eight-ball-with-foul': 'Derrota: a 8 caiu na mesma tacada em que houve falta.',
  'regras.fim.eight-ball-off-table': 'Derrota: a 8 pulou para fora da mesa.',
  'regras.fim.three-fouls': 'Derrota: terceira falta seguida do mesmo jogador.',
  'regras.fim.forfeit':
    'Desistência: quem desiste perde. Só vale depois da revelação da quebra; antes disso não há partida para desistir.',
  'regras.fim.all-balls-potted': 'A mesa esvaziou: vence quem tem mais pontos.',
  'regras.fim.score-unreachable':
    'A diferença de pontos passou do que ainda há na mesa: vence quem está na frente.',
  'regras.vitoria': 'vitória',
  'regras.derrota': 'derrota',
  'regras.placar': 'quem está na frente',

  // Decisões que a WPA dá ao jogador, com as opções na ordem gravada no replay.
  'regras.decisao.eight-on-break':
    'A 8 caiu na quebra. Não é falta nem derrota: o quebrador escolhe. Se também cometeu falta na tacada, quem escolhe é o adversário.',
  'regras.decisao.illegal-break':
    'A quebra não encaçapou nada nem levou bolas suficientes à tabela. O adversário escolhe — e, se houve falta na tacada, ainda leva a bola na mão.',
  'regras.decisao.opcoes': 'opções',
  'regras.decisao.semResposta': 'Sem resposta no prazo, vale a primeira opção.',

  // Prazos e o que acontece quando estouram.
  'regras.prazo.tacada': 'Prazo por tacada',
  'regras.prazo.tacadaExplica':
    'Estourou, a vez passa como uma tacada de força zero: falta por <a href="#falta-no-contact">não tocar em bola</a>. Bola na mão que não foi colocada vai para o ponto de saque. Não existe W.O.: quem sumiu perde turnos, e o jogo termina pelas regras.',
  'regras.prazo.revelacao': 'Revelação da quebra',
  'regras.prazo.revelacaoExplica':
    'Os dois jogadores revelam o segredo que sorteia a quebra. Se um não revelar a tempo, a partida é anulada e as entradas voltam pelo contrato.',
  'regras.prazo.duracao': 'Duração máxima',
  'regras.prazo.duracaoExplica':
    'Contada do início da partida. Estourou, ela acaba com vencedor: quem está na frente. Fica abaixo do prazo on-chain para a liquidação ainda caber dentro dele.',
  'regras.prazo.onChain': 'Prazo do contrato',
  'regras.prazo.onChainExplica':
    'Depois disso qualquer um dos dois pode pedir o depósito de volta. É a rede de segurança se o servidor sumir — a partida normal termina bem antes.',
  'regras.prazo.desconexao':
    'Cair da partida não é desistir: a vez do ausente vai passando, e quem voltar joga de onde está. Com os dois fora, o relógio para.',
  'regras.naFrente':
    'Quem está na frente: no 8-Ball, quem tem menos bolas do próprio grupo na mesa; na sinuca, quem tem mais pontos. Empatado, quem quebrou perde — a quebra é a vantagem de abrir a mesa.',

  // Limites do formato do replay, que também encerram a partida.
  'regras.limite.tacadas': 'tacadas',
  'regras.limite.posicoes': 'posicionamentos de bola na mão',
  'regras.limite.declaracoes': 'declarações de caçapa',
  'regras.limite.decisoes': 'decisões',
  'regras.limite.explica':
    'Toda partida é gravada num replay que cabe na transação de liquidação, e por isso ele tem teto. Quando o espaço acaba, a partida termina com vencedor: quem está na frente. Anular premiaria quem enrola.',
  'regras.fimServidor.regras': 'Terminou pelas regras: alguém encaçapou a 8 ou fechou o placar.',
  'regras.fimServidor.desistência': 'Um jogador desistiu.',
  'regras.fimServidor.tempo':
    'Estourou a duração máxima (vence quem está na frente) ou o prazo de revelação (anulada).',
  'regras.fimServidor.replay cheio': 'O replay encheu; vence quem está na frente.',
  'regras.segundos': '{n} s',
  'regras.minutos': '{n} min',

  // --------------------------------------------------------------- carteira
  'carteira.recusada': 'Solicitação recusada na carteira.',
  'carteira.foraDaRede': 'não está na rede {rede}',
  'carteira.soEnviando': 'só assina enviando pela própria rede',
  'carteira.semAssinatura': 'A carteira não devolveu assinatura.',
  'carteira.semTransacao': 'A carteira não devolveu a transação assinada.',

  // ------------------------------------------------------------------ saldo
  'saldo.criar': 'criar esta mesa',
  'saldo.entrar': 'entrar nesta mesa',
  'saldo.insuficiente':
    'Saldo insuficiente para {oQue}: a aposta é de {aposta} SOL e você tem {tem}. Contando a taxa da rede, seriam necessários cerca de {necessario} SOL.',

  // -------------------------------------------------------------- changelog
  //
  // Cada chave é montada em `versoes.ts` a partir do SLUG da versão (estável,
  // não muda se o nome mudar) e do índice da mudança — nunca do texto.
  'changelog.tituloPagina': 'SOL Pool — Novidades',
  'changelog.titulo': 'Novidades',
  'changelog.subtitulo': 'O que mudou no jogo, em ordem — do mais recente para o mais antigo.',
  'changelog.selo.rotulo': 'replay v{replay}',
  'changelog.selo.tituloAttr':
    'Esta versão mudou as regras: partidas gravadas a partir dela usam o formato {replay}.',

  // A física v7: a travadinha da quebra, que um jogador viu e a câmera lenta
  // expôs. O texto fala do que ele vê, não de `contatosEm`.
  'changelog.a-quebra-sem-travadinha.nome': 'A quebra sem travadinha',
  'changelog.a-quebra-sem-travadinha.resumo':
    'A mesa não para mais por um instante depois de a branca bater no triângulo — e todas as bolas param um pouco mais longe.',
  'changelog.a-quebra-sem-travadinha.mudanca.0.o':
    'Na quebra, a mesa inteira travava por um instante logo depois do contato e depois disparava. Acabou.',
  'changelog.a-quebra-sem-travadinha.mudanca.0.porque':
    'A física jogava fora o resto do passo depois de cada contato: as bolas tinham velocidade, mas o tempo não andava para ninguém. Na quebra, com os contatos em cascata, isso dava uma dúzia de passos seguidos parados.',
  'changelog.a-quebra-sem-travadinha.mudanca.1.o':
    'As bolas param em OUTRO lugar: na quebra de força máxima, 18 cm em média do ponto em que paravam antes; numa tacada de meia força, 15 cm; numa fraca, 4 cm. A quebra também encaçapa mais — de 1,4 para 1,7 bolas.',
  'changelog.a-quebra-sem-travadinha.mudanca.1.porque':
    'É o tempo que voltou. Cada contato perdia até 1/240 de segundo, e isso somava — por isso nenhuma tacada sai igual à de antes, nem as fracas. Não é que a bola corra muito mais: o caminho muda, e o destino com ele.',
  'changelog.a-quebra-sem-travadinha.mudanca.2.o':
    'As partidas jogadas antes desta versão continuam conferindo, com a física que as produziu.',
  'changelog.a-quebra-sem-travadinha.mudanca.2.porque':
    'Cada versão da física fica guardada. O replay diz em qual foi jogado, e é essa que o verifica — senão uma partida honesta do passado passaria a parecer fraudada.',

  // O mini jogo do treino, pedido do Bunker, e a mira que já valia na primeira
  // tacada de cada fase.
  'changelog.mini-jogo-no-treino.nome': 'Mini jogo no treino',
  'changelog.mini-jogo-no-treino.resumo':
    'A disputa da saída virou um treino sozinho, com alvo que muda de lugar — e a mira escolhida passou a valer desde a primeira tacada.',
  'changelog.mini-jogo-no-treino.mudanca.0.o':
    'Terceiro tipo de treino: Mini jogo. A mesma disputa da saída da partida, com a sua última e a sua melhor marca na barra.',
  'changelog.mini-jogo-no-treino.mudanca.0.porque':
    'A posição sai do mesmo sorteio da partida e a distância da mesma conta: o que se treina aqui é o que cai na mesa apostada.',
  'changelog.mini-jogo-no-treino.mudanca.1.o':
    'O alvo muda de lugar a cada 3 segundos, o tempo todo — também enquanto você mira. Vale o alvo aceso quando a bola 1 para.',
  'changelog.mini-jogo-no-treino.mudanca.1.porque':
    'Com alvo fixo, a disputa é só força. Os 3 segundos saíram de medir 2.700 tacadas: cada alvo corresponde a um tipo de tacada, então acertar o momento é escolha e não sorte. O botão Congelar deixa o alvo parado.',
  'changelog.mini-jogo-no-treino.mudanca.2.o':
    'No mini jogo do treino, a branca na caçapa é falta, como a bola 1.',
  'changelog.mini-jogo-no-treino.mudanca.2.porque':
    'Deixar a branca cair sem custo tornava a tacada forte sempre vantajosa. Isto vale só no treino, por enquanto.',
  'changelog.mini-jogo-no-treino.mudanca.3.o':
    'A mira que você escolheu vale desde o primeiro quadro do mini jogo e da quebra.',
  'changelog.mini-jogo-no-treino.mudanca.3.porque':
    'Cada fase da partida monta a mesa de novo, e ela nascia com a mira normal até a mensagem seguinte do servidor. Quem tacava logo via a linha errada.',

  // O torneio com pote, de ponta a ponta na devnet.
  'changelog.torneio-com-pote.nome': 'Torneio com pote',
  'changelog.torneio-com-pote.resumo':
    'O torneio passou a ter inscrição paga, com o dinheiro guardado na chain e o campeão levando o pote — e a melhor de 3 também.',
  'changelog.torneio-com-pote.mudanca.0.o':
    'Ao abrir um torneio, dá para definir a entrada. Quem entra paga uma vez, e o campeão leva o pote.',
  'changelog.torneio-com-pote.mudanca.0.porque':
    'O dinheiro fica num cofre na chain, não no servidor: quem pagou está provado na chain, e o servidor só confere.',
  'changelog.torneio-com-pote.mudanca.1.o':
    'A tela diz o pote pelo número de vagas antes de você assinar, e mostra o comprovante do pagamento depois.',
  'changelog.torneio-com-pote.mudanca.1.porque':
    'Antes o comprovante só aparecia no log do servidor — quem pagou não tinha o que mostrar.',
  'changelog.torneio-com-pote.mudanca.2.o':
    'A melhor de 3 também pode ter entrada paga, pelo mesmo cofre do torneio.',
  'changelog.torneio-com-pote.mudanca.2.porque':
    'Uma série de duas vagas é um torneio de duas vagas: mesmo cofre, mesma inscrição, mesma liquidação, sem instrução nova na chain.',
  'changelog.torneio-com-pote.mudanca.3.o':
    'Antes de assinar, a tela avisa que abrir a sala cobra o aluguel da conta do torneio, e que ele não volta.',
  'changelog.torneio-com-pote.mudanca.3.porque':
    'É dinheiro seu que não retorna nem na liquidação nem no cancelamento. Dizer é melhor que esconder.',

  // A mesa em pé no celular.
  'changelog.mesa-em-pe-no-celular.nome': 'Mesa em pé no celular',
  'changelog.mesa-em-pe-no-celular.resumo':
    'No celular em pé, a mesa gira e ocupa a tela toda, sem precisar virar o aparelho.',
  'changelog.mesa-em-pe-no-celular.mudanca.0.o':
    'A mesa aparece de pé quando o celular está de pé, usando a tela inteira.',
  'changelog.mesa-em-pe-no-celular.mudanca.0.porque':
    'Deitada numa tela em pé, a mesa ficava pequena no meio e sobrava preto em cima e embaixo.',
  'changelog.mesa-em-pe-no-celular.mudanca.1.o':
    'Com a mesa de pé, o dedo pega a bola onde ela aparece, e os números das bolas ficam de pé.',
  'changelog.mesa-em-pe-no-celular.mudanca.1.porque':
    'O toque era convertido sem o giro: a bola respondia a um ponto girado 90° do que a pessoa via.',

  'changelog.quebrar-de-novo-vale-quebra.nome': 'Quebrar de novo vale quebra',
  'changelog.quebrar-de-novo-vale-quebra.resumo':
    'Quando a mesa é rearmada, a nova quebra usa o bônus do taco e a régua de força inteira, como a primeira.',
  'changelog.quebrar-de-novo-vale-quebra.mudanca.0.o':
    'Quebrar de novo agora vale como quebra: o taco usa o bônus de quebra e a régua de força vai até o fim.',
  'changelog.quebrar-de-novo-vale-quebra.mudanca.0.porque':
    'Antes, a quebra repetida saía mais fraca — sem o bônus do taco e com a régua cortada — e dava vantagem a quem forçava o rearme.',

  // A mira: o defeito que um jogador descreveu como "a rebatida nunca vai para
  // a direção certa", e as duas causas dele.
  // O modo de mira por mesa: um número, e os três modos são presets dele.
  'changelog.mira-rapida-e-relogio-no-meio.nome': 'Mira rápida e relógio no meio',
  'changelog.mira-rapida-e-relogio-no-meio.resumo':
    'Um clique aponta a mira, o tempo da tacada fica à vista e o pano fica livre para olhar as bolas.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.0.o':
    'Clicar num ponto do pano leva a mira até ele. Arrastar continua igual.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.0.porque':
    'O clique acerta o grosso, e o Shift cuida dos milímetros.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.1.o':
    'O relógio da tacada fica grande no meio do placar e muda de cor: verde, amarelo a partir de 20 s e vermelho a partir de 10 s. Embaixo dele, o pote da mesa.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.1.porque':
    'No topo, o tempo acabando passava despercebido.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.2.o':
    'O pedido de caçapa saiu do meio do pano e foi para o placar, em dourado. As caçapas continuam piscando.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.2.porque':
    'O aviso no pano escondia justamente as bolas que você precisa olhar para escolher a caçapa.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.3.o':
    'A calha mostra as bolas na ordem em que caíram.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.3.porque':
    'Como numa canaleta de verdade, a fileira conta a história da partida.',
  'changelog.a-mesa-que-se-explica.nome': 'A mesa que se explica',
  'changelog.a-mesa-que-se-explica.resumo':
    'Estatísticas do adversário a um toque, um fim de partida que diz o que fazer, e menus mais arrumados.',
  'changelog.a-mesa-que-se-explica.mudanca.0.o':
    'Numa mesa em rede, tocar no cartão de um jogador no placar mostra o aproveitamento, as últimas partidas e a posição na semana.',
  'changelog.a-mesa-que-se-explica.mudanca.0.porque':
    'Dá para saber se o adversário é bom sem sair da mesa. O cartão não pausa nada e fecha sozinho em 10 segundos.',
  'changelog.a-mesa-que-se-explica.mudanca.1.o':
    'No fim da partida, "Voltar ao lobby" virou o botão principal do cartão.',
  'changelog.a-mesa-que-se-explica.mudanca.1.porque':
    'Era um link cinza no meio do cartão e passava despercebido.',
  'changelog.a-mesa-que-se-explica.mudanca.2.o':
    'O menu do treino ficou em blocos com título: modalidade, nova partida, idioma e como tacar.',
  'changelog.a-mesa-que-se-explica.mudanca.3.o':
    'O chat não deixa mais um vão entre as mensagens.',
  'changelog.a-mesa-que-se-explica.mudanca.4.o':
    'Apertar Shift no meio do arraste não faz mais a mira nem a força recuarem.',
  'changelog.a-mesa-que-se-explica.mudanca.4.porque':
    'O ajuste fino recalculava todo o caminho já feito com a velocidade nova, e a mira saltava uns 30° para trás. Agora ele vale só daqui em diante.',
  'changelog.disputa-da-saida.nome': 'A disputa da saída',
  'changelog.disputa-da-saida.resumo':
    'Quem estoura as bolas agora se decide na mesa, num mini jogo de precisão antes de cada partida.',
  'changelog.disputa-da-saida.mudanca.0.o':
    'Antes da quebra, os dois tacam a mesma posição: quem deixar a bola colorida mais perto do centro do alvo estoura.',
  'changelog.disputa-da-saida.mudanca.0.porque':
    'Até aqui quem criava a sala estourava sempre. A quebra é vantagem, e agora ela é conquistada.',
  'changelog.disputa-da-saida.mudanca.1.o':
    'A posição das bolas e do alvo muda a cada partida, e ninguém a escolhe: ela sai do mesmo sorteio a dois que já decidia a quebra.',
  'changelog.disputa-da-saida.mudanca.1.porque':
    'Com posição fixa, bastaria decorar uma tacada. E com o sorteio a dois, nenhum jogador consegue puxar a posição para o seu lado.',
  'changelog.disputa-da-saida.mudanca.2.o':
    'Empate repete a disputa com a ordem invertida, até três rodadas. Quem não tacar em 60 segundos perde a disputa.',
  'changelog.disputa-da-saida.mudanca.2.porque':
    'A física é exata: a mesma tacada termina sempre no mesmo lugar, então o segundo poderia copiar o primeiro. Inverter a ordem tira a graça de copiar.',
  'changelog.a-cacapa-em-toda-tacada.nome': 'A caçapa em toda tacada',
  'changelog.a-cacapa-em-toda-tacada.resumo':
    'No 8-Ball, depois da quebra, você anuncia a caçapa antes de cada tacada.',
  'changelog.a-cacapa-em-toda-tacada.mudanca.0.o':
    'Depois da quebra, toda tacada começa escolhendo a caçapa: as seis pulsam até você tocar numa, e o taco aparece em seguida.',
  'changelog.a-cacapa-em-toda-tacada.mudanca.0.porque':
    'Acaba a tacada a esmo para derrubar qualquer bola: vale o que você anunciou.',
  'changelog.a-cacapa-em-toda-tacada.mudanca.1.o':
    'Se uma bola sua cair na caçapa escolhida, a tacada vale, e as outras que caírem junto também. Se nenhuma cair nela, a vez passa, sem falta.',
  'changelog.a-cacapa-em-toda-tacada.mudanca.1.porque':
    'Errar a caçapa é errar a jogada, não cometer infração: o adversário ganha a vez, mas não a bola na mão. Com a mesa aberta, o seu grupo é o da bola que caiu na caçapa escolhida.',
  'changelog.a-cacapa-em-toda-tacada.mudanca.2.o':
    'Partidas gravadas antes continuam tocando com a regra de quando foram jogadas.',
  'changelog.a-cacapa-em-toda-tacada.mudanca.2.porque':
    'Cada replay guarda a versão com que foi gravado, e a versão diz com quais regras reproduzi-lo.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.nome': 'Força fina e bola na mão que acompanha',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.resumo':
    'A régua de força deixou de pular de bloco em bloco, a branca na mão segue o ponteiro e a mesa ganhou acabamento novo.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.0.o':
    'Qualquer ponto da régua de força vale. Os 10 blocos ficam como marcas, e o número embaixo vira porcentagem.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.0.porque':
    'Com 10 níveis não existia força para parar a branca a 80 cm. Cada marca continua valendo exatamente o que o nível valia, só que agora o que fica entre elas também existe.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.1.o':
    'Com bola na mão depois de uma falta, a branca segue o ponteiro e um clique a coloca. O adversário a vê se mexendo.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.1.porque':
    'Quando a branca voltava ao ponto de saque, só dava para movê-la clicando em cima dela. Clicar em outro lugar virava mira, e o relógio seguia correndo.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.2.o':
    'As caçapas ganharam entrada forrada de pano e aro nítido, e os diamantes foram para o meio da madeira, em marfim.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.3.o':
    'Se a partida não começar em 2 minutos, a mesa mostra o motivo, quanto falta para o reembolso automático e um botão para voltar ao lobby.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.3.porque':
    'Antes a espera girava sem dizer nada por até uma hora, com os dois depósitos parados no contrato.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.4.o':
    'No lobby, a sua mesa mostra em quanto tempo os depósitos voltam sozinhos se a partida não começar.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.4.porque':
    'Assim ninguém fica achando que perdeu a entrada.',
  'changelog.cada-mesa-escolhe-a-mira.nome': 'Cada mesa escolhe a sua mira',
  'changelog.cada-mesa-escolhe-a-mira.resumo':
    'Quem cria a mesa decide quanta linha-guia ela mostra — de nenhuma até a trajetória inteira.',
  'changelog.cada-mesa-escolhe-a-mira.mudanca.0.o':
    'Ao criar uma mesa dá para escolher a mira: sem mira, normal ou estendida.',
  'changelog.cada-mesa-escolhe-a-mira.mudanca.0.porque':
    'Sem mira a tela não mostra nada da tacada, nem onde a branca vai tocar. Normal é a mesa de sempre. Estendida mostra a trajetória inteira com as tabelas, então dá para ver se a bola entra antes de tacar. Quem prefere jogar de olho não precisa mais fingir que a linha não está lá, e quem está aprendendo pode ligar a ajuda toda.',
  'changelog.cada-mesa-escolhe-a-mira.mudanca.1.o':
    'A escolha vale para os DOIS jogadores, e aparece no lobby antes de você depositar.',
  'changelog.cada-mesa-escolhe-a-mira.mudanca.1.porque':
    'Ver a trajetória da bola até a caçapa é informação que o adversário não tem — numa mesa com SOL em jogo, isso não pode ser escolha de um lado só. O modo é da mesa, imutável depois de criada, e a listagem mostra qual é para você saber no que está entrando.',
  'changelog.cada-mesa-escolhe-a-mira.mudanca.2.o':
    'Também dá para digitar um alcance qualquer, em metros.',
  'changelog.cada-mesa-escolhe-a-mira.mudanca.2.porque':
    'Os três modos são atalhos de um mesmo número: quanto do caminho depois do contato a tela desenha. Quem quiser um meio-termo — meio metro, por exemplo — digita o valor e a mesa nasce assim.',
  'changelog.a-mira-que-nao-mentia-mais.nome': 'A mira que não mente mais',
  'changelog.a-mira-que-nao-mentia-mais.resumo':
    'A tacada vai para onde a linha aponta, a mira ficou fina de verdade, e ela deixou de ser atributo de taco.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.0.o':
    'A tacada agora sai exatamente na direção que a linha-guia mostra.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.0.porque':
    'O taco só conseguia apontar em direções de meio em meio grau, e a linha na tela era desenhada sem esse arredondamento. Você mirava na caçapa, a tela confirmava, e a bola saía até um quarto de grau ao lado — o que numa tacada longa é mais que a boca da caçapa. Havia posições em que NENHUMA direção possível acertava, por mais que você tentasse.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.1.o':
    'A mira gira devagar, e no mesmo ritmo em qualquer lugar da tela.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.1.porque':
    'A sensibilidade dependia de quão longe do taco estava o seu dedo: perto, cada pixel valia mais de um grau; longe, um décimo disso. A mesma mexidinha fazia coisas muito diferentes. Agora o passo é constante, e em tacada longa fica automaticamente mais fino — dá para parar dentro da janela que faz a bola cair. O ajuste fino continua no Shift e no botão, para quando você quiser ainda mais.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.2.o':
    'A mira não é mais atributo de taco: todos apontam igual.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.2.porque':
    'Precisão era a única coisa que o taco comprado dava e que a mão não conseguia compensar — e, pior, era o que estava quebrado. Quem joga com o taco de graça mira exatamente como quem cunhou um lendário. O que os tacos ainda diferenciam é peso, efeito, aderência e quebra.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.3.o':
    'Os tacos foram rebalanceados para quatro atributos, e os níveis de mira já comprados viraram aderência.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.3.porque':
    'Os orçamentos de cada raridade eram a soma dos cinco atributos; sem a mira eles foram re-ancorados na soma dos quatro que ficaram, mantendo o taco padrão exatamente onde estava. Ninguém perde o que pagou: cada nível de mira comprado virou um nível de aderência, porque a curva de preço é por atributo e descartá-los faria você pagar duas vezes.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.4.o':
    'O taco "Preciso" agora se chama "Controle".',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.4.porque':
    'A identidade dele era a mira mais fina da coleção, e isso deixou de existir. Ele passou a ser o taco que segura a branca onde você a deixou: a maior aderência do catálogo. É o mesmo item, com o mesmo histórico — só o nome e os números mudaram.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.nome':
    'O som da mesa, e a força no lugar',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.resumo':
    'As bolas passam a soar enquanto correm, e a tacada mais forte volta a ser uma tacada.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.0.o':
    'Dá para ouvir as bolas rolando e raspando no pano, e o som muda com a velocidade.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.0.porque':
    'Rolar e patinar são coisas diferentes de ouvir, e a física já distinguia as duas: a bola recém-tacada patina até o atrito alinhar o giro, e só depois rola. Numa quebra o som começa como raspagem e vira ronco.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.1.o':
    'A força máxima de uma tacada comum deixou de ser mais forte que a quebra de um profissional.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.1.porque':
    'Força total valia 26,8 mph em qualquer tacada — acima da quebra mais forte que se mede num campeonato. Com recuo, a branca voltava a 20 mph, atravessava a mesa e quicava na tabela. A quebra continua usando o topo; o resto, não.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.2.o':
    'Na quebra, dá para largar a branca em cima da linha de demarcação.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.2.porque':
    'A tela prendia a bola um raio antes da linha, como se ela fosse uma tabela, enquanto o servidor sempre julgou pelo centro. Isso apagava 6% da cozinha — e logo a faixa mais perto do triângulo, que é a que serve.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.3.o':
    'Usar a barra de força não trava mais a mira daí em diante.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.3.porque':
    'Depois de arrastar a barra uma vez, todo arrasto seguinte continuava valendo como força, e a mira não girava mais até recarregar a página.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.4.o':
    'Depois da primeira tacada, a mira volta a girar.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.4.porque':
    'O ponteiro ficava preso sem ninguém arrastando, e a partir dali todo toque parecia começar no mesmo lugar — perto da branca, ou seja, sempre no taco. O jogo parecia ter caído.',
  'changelog.quebra-com-a-branca-na-mao.nome': 'Quebra com a branca na mão',
  'changelog.quebra-com-a-branca-na-mao.resumo':
    'A quebra passa a seguir a WPA: quem quebra escolhe de onde sai.',
  'changelog.quebra-com-a-branca-na-mao.mudanca.0.o':
    'Na quebra, arraste a branca para qualquer ponto atrás da linha da cabeça.',
  'changelog.quebra-com-a-branca-na-mao.mudanca.0.porque':
    'É a regra da WPA, e a quebra é a tacada que mais depende do ponto de saída — ' +
    'obrigar todo mundo a sair do mesmo lugar empobrece justamente a tacada em que ' +
    'os dois lados têm a mesma chance.',
  'changelog.quebra-com-a-branca-na-mao.mudanca.1.o':
    'Quem não quiser mover a branca quebra do ponto de saque, como sempre.',
  'changelog.quebra-com-a-branca-na-mao.mudanca.1.porque':
    'Mover é direito, não etapa: ninguém precisa de um toque a mais por rack.',
  'changelog.quebra-com-a-branca-na-mao.mudanca.2.o':
    'Partidas gravadas antes desta versão continuam se provando exatamente como antes.',
  'changelog.quebra-com-a-branca-na-mao.mudanca.2.porque':
    'Um replay antigo é reproduzido com as regras da época em que foi jogado. Sem isso, ' +
    'mudar uma regra apagaria o passado — e há partidas liquidadas na chain que dependem ' +
    'de continuarem verificáveis.',

  'changelog.mirar-e-tacar-separados.nome': 'Mirar e tacar separados',
  'changelog.mirar-e-tacar-separados.resumo':
    'Dois gestos em vez de um, e o modo antigo continua disponível.',
  'changelog.mirar-e-tacar-separados.mudanca.0.o':
    'Arraste no pano para girar a mira; pegue no taco e puxe para tacar.',
  'changelog.mirar-e-tacar-separados.mudanca.0.porque':
    'No celular, um arrasto que mirava e tacava ao mesmo tempo fazia com que conferir ' +
    'a pontaria com o dedo mandasse a bola e perdesse a vez.',
  'changelog.mirar-e-tacar-separados.mudanca.1.o':
    'A barra de força também escolhe a força: arraste nela e solte.',
  'changelog.mirar-e-tacar-separados.mudanca.1.porque':
    'Foi o que os jogadores tentaram fazer sozinhos — uma barra na tela convida ao gesto.',
  'changelog.mirar-e-tacar-separados.mudanca.2.o':
    'O modo antigo — um arrasto resolve tudo — fica no menu, em "Como tacar".',
  'changelog.mirar-e-tacar-separados.mudanca.2.porque':
    'No mouse ele é mais rápido, e é o que quem já jogava tem no dedo. O padrão vem do ' +
    'aparelho: dedo recebe os dois gestos, mouse recebe o arrasto único.',
  'changelog.mirar-e-tacar-separados.mudanca.3.o':
    'A linha de previsão aparece enquanto for a sua vez, e não só com o taco puxado.',
  'changelog.mirar-e-tacar-separados.mudanca.3.porque':
    'Com mira e força separadas, ela sumia justamente na hora de escolher para onde apontar.',

  'changelog.a-tela-e-a-mesa.nome': 'A tela é a mesa',
  'changelog.a-tela-e-a-mesa.resumo':
    'O cabeçalho e o painel saíram do caminho; o pano ocupa a janela inteira.',
  'changelog.a-tela-e-a-mesa.mudanca.0.o':
    'Botões nos cantos, por cima do pano: menu e conversa à esquerda, tacada à direita.',
  'changelog.a-tela-e-a-mesa.mudanca.0.porque': 'Numa mesa de sinuca, área de tela é precisão de mira.',
  'changelog.a-tela-e-a-mesa.mudanca.1.o':
    'A régua de força mudou para a direita, do lado da mão que taca.',
  'changelog.a-tela-e-a-mesa.mudanca.2.o':
    'O controle de efeito virou botão: a branca no topo mostra o efeito escolhido e abre grande.',
  'changelog.a-tela-e-a-mesa.mudanca.2.porque':
    'Ele ocupava faixa fixa na lateral para uma escolha que não se muda toda tacada.',
  'changelog.a-tela-e-a-mesa.mudanca.3.o':
    'Em tela larga, a conversa encaixa ao lado da mesa em vez de cobri-la.',
  'changelog.a-tela-e-a-mesa.mudanca.4.o':
    'No celular, o botão "Deitar a tela" tenta girar o aparelho e, onde o navegador não deixa, gira o desenho.',
  'changelog.a-tela-e-a-mesa.mudanca.4.porque':
    'No navegador embutido das carteiras e no Safari do iPhone não existe API de girar — ' +
    'e é lá que este jogo é aberto com mais frequência.',
  'changelog.a-tela-e-a-mesa.mudanca.5.o':
    'A branca deixou de ter pontos vermelhos: agora são cinza-claros.',
  'changelog.a-tela-e-a-mesa.mudanca.5.porque':
    'A pergunta foi "por que a branca não é branca?". Lisa ela não pode ser, senão o efeito ' +
    'aplicado fica invisível.',

  'changelog.compat.titulo': 'Partidas antigas continuam valendo',
  'changelog.compat.intro':
    'Mudar uma regra não pode apagar o passado: um replay é reproduzido com as regras da ' +
    'época em que foi jogado.',
  'changelog.compat.texto': {
    one: 'Hoje o jogo <strong>grava</strong> no formato <span class="tabular-nums">{grava}</span> e <strong>lê</strong> o formato <span class="tabular-nums">{le}</span>.',
    other:
      'Hoje o jogo <strong>grava</strong> no formato <span class="tabular-nums">{grava}</span> e <strong>lê</strong> os formatos <span class="tabular-nums">{le}</span>.',
  },
  'changelog.compat.rodape': 'Estes dois números saem do próprio código, e não deste texto.',
} as const
